/*

& Every thunk the web dispatches (API calls and state setters).

  10 Oct 2026: the delete_account slice (the old delete-link token) and the thunks and
  reducers that served the old delete flows (deleteBusinessProfile, sendDeleteBPLink) were
  removed; deletion now uses a server-sent code (lib/teams/deleteFlowsClient).

*/

/*
? Let's import the global slice and the thunk type
*/

import { AlertColor, AlertPropsColorOverrides } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";
import { ObjectId } from "mongodb";
import { AnyAction } from "redux";
import { output_type_of_database_query } from "../lib/data/search-filters/helpers/buildDatabaseQueries";
import StateInterface from "./state-interface";
import { AppThunk, globalSlice } from "./store";
import { PropertyBoostInterface } from "./sub-interfaces/boost-interface";
import { LastMessageInterface, NotificationSettingInterface, Participants } from "./sub-interfaces/chat-interface";
import { BeegruUserProfileContext, BeegruUserSubscriptionInformation } from "./sub-interfaces/redux-session-interface";
import { SearchFilterId, SearchFilterOption } from "./sub-interfaces/search-types";
import {
	BoostInterface,
	BoostLocation,
	ChatStatesInterface,
	profile_contextInterface,
	ProfilePicture,
	PropertyInterface,
	SearchFilterMetrices,
	service_images,
} from "./sub-interfaces/sub-interfaces";

/*
& Now let's describe a thunk for each reducer in the redux-magic/reducers.ts file.
*/

export const changeMeThunk =
	(payload: string): AppThunk =>
	async (dispatch) => {
		/*
    & Let's simulate a long-running task like a network request by awaiting a promise which resolves inside a setTimeout.
    */

		await new Promise<void>((resolve) => {
			const timeout = setTimeout(() => {
				clearTimeout(timeout);
				resolve();
			}, 3000);
		});

		/*
    & Now, we'll dispatch the action to change the state.
    */

		dispatch(globalSlice.actions.changeMe(payload));
	};

export const fetchSinglePropertyThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + slug, {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const setThemeThunk =
	(theme: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setThemeReducer(theme));
	};

export const CreateNewPostThunk =
	(payload: {
		_id: string | ObjectId;
		creator_is_business_profile: boolean;
		creator_user_id: string | ObjectId;
		creator_business_profile_id: string | ObjectId;
		body: string;
		file_id?: Array<ObjectId | null>;
		privacy_status: string;
		search_string: string;
		search_points: number;
		video?: boolean;
		is_blasted: boolean;
		blasted_post: string | ObjectId;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CreateNewPost(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "posts/create-new-post", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const CreateNewCommentsThunk =
	(payload: {
		post_id: string | ObjectId;
		creator_is_business_profile: boolean;
		creator_user_id: string | ObjectId;
		creator_business_profile_id: string | ObjectId;
		name: string;
		url: string;
		tabValue: number;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CreateNewComments({
				tabValue: payload.tabValue,
				response: await await (
					await fetch(process.env.PRODUCTION_API_URL + "posts/comments/add-comment", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			}),
		);
	};

export const setImageCategoryThunk =
	(payload: StateInterface["category"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setImageCategory(payload));
	};
// export const VerifyEmailThunk =
//   (payload: StateInterface["verify_email"]): AppThunk =>
//   async (dispatch) => {
//     dispatch(globalSlice.actions.VerifyEmail(payload));
//   };

export const setLanguageThunk =
	(payload: StateInterface["default_language"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setLanguage(payload));
	};

export const setImagesIndexThunk =
	(payload: StateInterface["images_index"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setImagesIndex(payload));
	};

export const KycProfileTypesThunk =
	(payload: StateInterface["kyc_types"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.KycProfileTypes(payload));
	};

export const setTabValueThunk =
	(payload: StateInterface["tabValue"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setTabValue(payload));
	};
export const setSaveTabValueThunk =
	(payload: StateInterface["saved_tabValue"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setSaveTabValue(payload));
	};
export const setListingTabValueThunk =
	(payload: StateInterface["listing_tabValue"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setListingTabValue(payload));
	};
export const setAnalyticsTabValueThunk =
	(payload: StateInterface["analytics_tabValue"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setAnalyticsTabValue(payload));
	};
export const setUserLocationThunk =
	(payload: StateInterface["user_location"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setUserLocation(payload));
	};

export const fetchSingleBusinessProfileThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleBusinessProfile(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/" + slug, {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchGlobalSearchResultsThunk =
	(payload: StateInterface["search_query"]): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchGlobalSearchResults(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "search", {
						method: "POST",
						headers: {
							cache: "no-store",
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreGlobalSearchResultsThunk =
	(payload: StateInterface["search_query"]): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreGlobalSearchResults(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "search", {
						method: "POST",
						headers: {
							cache: "no-store",
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreGlobalSearchPostsThunk =
	(payload: StateInterface["search_query"]): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreGlobalSearchPosts(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "search", {
						method: "POST",
						headers: {
							cache: "no-store",
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setIsSearchingThunk =
	(payload: StateInterface["is_searching"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setIsSearching(payload));
	};

export const setSearchResultsPageNumberThunk =
	(payload: StateInterface["search_results_page_number"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setSearchResultsPageNumber(payload));
	};

export const setIsLoadingThunk =
	(payload: StateInterface["is_loading"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setIsLoading(payload));
	};

export const setOpenPostModalThunk =
	(payload: StateInterface["open_post_modal"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setOpenPostModal(payload));
	};

export const CreateNewReviewThunk =
	(payload: {
		source: string;
		payload: {
			sessionId: string | ObjectId;
			reviewed_entity_type: string;
			reviewed_entity_id: string | ObjectId;
			location_title: string;
			location_coordinates: Array<number>;
			rating: number;
			body: string;
			creator_is_business_profile: boolean;
			creator_user_id: string | ObjectId;
			creator_business_profile_id: string | ObjectId;
			url: string;
		};
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CreateNewReview({
				source: payload.source,
				response: await (
					await fetch(process.env.PRODUCTION_API_URL + "reviews/create-new-review", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload.payload),
					})
				).json(),
			}),
		);
	};

export const fetchPropertyReviewsPageThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchPropertyReviewsPageData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + slug + "/reviews", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const SetintroThunk =
	(payload: StateInterface["intro"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.Setintro(payload));
	};

export const StoreLeadsThunk =
	(payload: {
		name: string;
		phone: {
			country_code: string;
			phone_number: string;
		};
		agreed_to_tc: boolean;
		source: string;
		listing_slug: string | undefined;
		listing_type: string;
		listing_id: string | ObjectId | undefined;
		is_guest: boolean;
		listing_title: string | undefined;
		listing_owner: {
			is_business_profile: boolean | undefined;
			user_id: string | ObjectId | undefined;
			business_profile_id: string | ObjectId | undefined;
		};
		last_enquiry_at: Date | string;
		user_id: string | ObjectId;
		// The acting profile the enquiry is from (19 Oct 2026); the business id only when acting as one.
		is_business_profile?: boolean;
		business_profile_id?: string | ObjectId;
		is_lead_from_boost: boolean;
		created_by: {
			is_business_profile: boolean | undefined;
			user_id: string | ObjectId | undefined;
			business_profile_id: string | ObjectId | undefined;
		};
		notes: string;
		assigned_to: Array<string | ObjectId>;
		reminders: Array<{
			trigger_reminder_at: Date | string;
			reminder_at: Date | string;
			title: string;
			created_at: Date | string;
		}>;
		status: string;
		is_manual_lead?: boolean;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.storeleads(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/lead/store-cta-lead", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

/* The dashboard's Add Lead (business-dashboard/leads/add-lead, lib/leads/addManualLead.ts): a team
   action, unlike StoreLeadsThunk (a visitor's enquiry). A number that already has a lead on the
   listing comes back as `existingLeadId` and nothing is written. */
export const AddDashboardLeadThunk =
	(
		payload: Parameters<typeof StoreLeadsThunk>[0],
	): AppThunk<Promise<{ ok: boolean; existingLeadId: string | null; error: string | null }>> =>
	async (dispatch) => {
		try {
			const result = await (
				await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/leads/add-lead", {
					method: "POST",
					cache: "no-store",
					headers: { "Content-Type": "text/plain" },
					body: JSON.stringify(payload),
				})
			).json();
			if (result?.status === "success") {
				if (result.payload) dispatch(globalSlice.actions.storeleads(result));
				return { ok: true, existingLeadId: null, error: null };
			}
			return {
				ok: false,
				existingLeadId: result?.error === "lead-exists" ? String(result?.payload?._id || "") : null,
				error: result?.error || null,
			};
		} catch {
			return { ok: false, existingLeadId: null, error: "Network error" };
		}
	};

export const UpdateLeadsThunk =
	(payload: {
		_id: string | ObjectId;
		name: string;
		phone: {
			country_code: string;
			phone_number: string;
		};
		agreed_to_tc: boolean;
		source: string;
		listing_slug: string | undefined;
		listing_type: string;
		listing_id: string | ObjectId | undefined;
		is_guest: boolean;
		listing_title: string | undefined;
		listing_owner: {
			is_business_profile: boolean | undefined;
			user_id: string | ObjectId | undefined;
			business_profile_id: string | ObjectId | undefined;
		};
		user_id: string | ObjectId;
		is_lead_from_boost: boolean;
		created_by: {
			is_business_profile: boolean | undefined;
			user_id: string | ObjectId | undefined;
			business_profile_id: string | ObjectId | undefined;
		};
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateleads(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/lead/update-cta-lead", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSingleUserProfileThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleUserProfile(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/" + slug, {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchBoostPostPageThunk =
	(payload: StateInterface["boost_post_page"]): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBoostPostPageData(
				await (await fetch(process.env.PRODUCTION_API_URL + "boosting/boost-post-page-data?post_id=" + payload)).json(),
			),
		);
	};

export const fetchBoostUserProfilePageThunk =
	(payload: StateInterface["boost_user_profile_page"]): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBoostUserProfilePageData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "boosting/boost-user-profile-page?user_id=" + payload)
				).json(),
			),
		);
	};
export const fetchBoostBusinessProfilePageThunk =
	(payload: StateInterface["boost_business_profile_page"]): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBoostBusinessProfilePageData(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "boosting/boost-business-profile-page?business_profile_id=" + payload,
					)
				).json(),
			),
		);
	};

export const fetchBoostPropertyPageThunk =
	(payload: StateInterface["boost_property_page"]): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBoostPropertyPageData(
				await (await fetch(process.env.PRODUCTION_API_URL + "boosting/boost-property-page?slug=" + payload)).json(),
			),
		);
	};

export const fetchSingleServicePageThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleServicePageData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/" + slug, {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchNeighbourhoodReviewsPageThunk =
	(payload: { location?: string; coords?: string; sessionId: any }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchNeighbourhoodReviewsPageData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "neighbourhood-reviews", {
						method: "POST",
						headers: {
							cache: "no-store",
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchServiceReviewsPageThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchServiceReviewsPageData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/" + slug + "/reviews", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchBoostServicePageThunk =
	(payload: StateInterface["boost_service_page"]): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBoostServicePageData(
				await (await fetch(process.env.PRODUCTION_API_URL + "boosting/boost-service-page?slug=" + payload)).json(),
			),
		);
	};
export const fetchHomePageThunk =
	({ sessionId, looking_for }: { sessionId: string; looking_for?: any }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchHomePageData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "home", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId, looking_for, location }),
					})
				).json(),
			),
		);
	};

export const fetchNotificationPageThunk =
	(payload: {
		sessionId: string;
		profile_context_is_business_profile: boolean;
		profile_context_user_id: string | ObjectId;
		profile_context_business_profile_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchNotificationPage(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "notifications/fetch-notifications", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreNotificationPageThunk =
	(payload: {
		profile_context_is_business_profile: boolean;
		profile_context_user_id: string | ObjectId;
		profile_context_business_profile_id: string | ObjectId;
		skip: number;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreNotificationPage(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "notifications/load-more-notifications", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchRealtimeNotificationThunk =
	(payload: {
		sessionId: string;
		notification_id: ObjectId | string;
		profile_context_is_business_profile: boolean;
		profile_context_user_id: string | ObjectId;
		profile_context_business_profile_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchRealtimeNotification(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "notifications/fetch-realtime-notification", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateNotificationViewedThunk =
	(payload: {
		notification_id: ObjectId | string;
		profile_context_is_business_profile: boolean;
		profile_context_user_id: string | ObjectId;
		profile_context_business_profile_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateNotificationViewed(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "notifications/update-notification-viewed", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateNotificationsViewedThunk =
	(payload: {
		notification_ids: Array<ObjectId | string> | null;
		profile_context_is_business_profile: boolean;
		profile_context_user_id: string | ObjectId;
		profile_context_business_profile_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateNotificationsViewed(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "notifications/update-notifications-viewed", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchFaqsThunk = (): AppThunk => async (dispatch) => {
	dispatch(globalSlice.actions.fetchFaqs(await (await fetch(process.env.PRODUCTION_API_URL + "faqs")).json()));
};

export const fetchCategoryFaqsThunk =
	({ category }: { category: string }): AppThunk =>
	async (dispatch) => {
		const response = await fetch(process.env.PRODUCTION_API_URL + "faqs/category/" + category);
		const data = await response.json();
		return data;
	};

export const setFaqSearchResultsThunk =
	(payload: Array<StateInterface["faq"]>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setFaqSearchResults(payload));
	};

export const fetchSingleFaqThunk =
	({ slug }: { slug: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleFaq(await (await fetch(process.env.PRODUCTION_API_URL + "faqs/" + slug)).json()),
		);
	};

export const FaqAddUpvoteThunk =
	(payload: {
		faq_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.FaqAddUpvote(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "faqs/add-upvote", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const FaqAddDownvoteThunk =
	(payload: {
		faq_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.FaqAddDownvote(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "faqs/add-downvote", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const FaqRemoveUpvoteThunk =
	(payload: {
		faq_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.FaqRemoveUpvote(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "faqs/remove-upvote", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const FaqRemoveDownvoteThunk =
	(payload: {
		faq_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.FaqRemoveDownvote(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "faqs/remove-downvote", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

/*
 * Unused, and its route (pages/api/business-profiles/add-admin.ts) now answers 404: it added
 * people straight onto a team without an invite. Invites go through
 * business-dashboard/business-profile-admins/add-business-profile-admin.
 */
export const addAdminUserDetailsThunk =
	(payload: {
		business_profile_id: string;
		editor_user_id: string;
		access_level: string;
		url: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.addAdminUserDetails(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/add-admin", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const deleteAdminUserDetailsThunk =
	(payload: { business_profile_id: string; editor_user_id: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.deleteAdminUserDetails(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/remove-admin", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateBusinessAboutThunk =
	(payload: {
		sessionId: string | ObjectId;
		_id: string | ObjectId;
		description: string;
		phone_number: string;
		country_code: string;
		email: string;
		year_established: string | number | Date;
		website_url: string;
		office_address: string | Array<{}>;
		industry_type: string;
		url: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessAbout(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/update-about", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateBusinessBrokerageThunk =
	(payload: { _id: string; agent_brokerage: Array<{}>; url: string; sessionId: ObjectId | string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessBrokerage(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/update-brokerage", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateBusinessExpertiseThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		expertise: Array<string> | [];
		url: string;
		sessionId: string | ObjectId;
		profile_context: profile_contextInterface;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessExpertise(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/profile/update-expertise", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateBusinessLanguagesThunk =
	(payload: {
		_id: string;
		language: Array<{ language: string; proficiency: string }>;
		url: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessLanguages(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/update-languages", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateBusinessProfileShortDescriptionThunk =
	(description: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.UpdateBusinessProfileShortDescription(description));
	};

export const UpdateBusinessProfilecountry_codeThunk =
	(country_code: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.UpdateBusinessProfilecountry_code(country_code));
	};

export const UpdateBusinessProfilePhoneThunk =
	(phone: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.UpdateBusinessProfilePhone(phone));
	};

export const UpdateBusinessProfileEmailThunk =
	(email: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.UpdateBusinessProfileEmail(email));
	};

export const UpdateBusinessProfileEstDateThunk =
	(EstDate: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.UpdateBusinessProfileEstDate(EstDate));
	};

export const UpdateBusinessProfileWebsiteThunk =
	(Website: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.UpdateBusinessProfileWebsite(Website));
	};

export const UpdateBusinessProfileOfficeAddressesThunk =
	(OfficeAddresses: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.UpdateBusinessProfileOfficeAddresses(OfficeAddresses));
	};

export const UpdateBusinessServicesThunk =
	(payload: {
		_id: string | ObjectId;
		services: Array<string> | [];
		url: string;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessServices(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/update-services", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateBusinessFAQsThunk =
	(payload: {
		_id: string;
		faqs: Array<{ question: string; answer: string }>;
		url: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessFAQs(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/update-faqs", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const AddSepcialOfferDetailsForBusinessProfileThunk =
	(payload: {
		_id: string;
		title: string;
		description: string;
		media_url: string;
		mime_type: string;
		from_date: string;
		to_date: string;
		eligibility_criteria: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.AddSepcialOfferDetailsForBusinessProfile(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/add-special-offers", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateBusinessLicencesThunk =
	(payload: { _id: string; licences: Array<{}>; url: string; sessionId: ObjectId | string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessLicences(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/update-licenses-certifications", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateUserAboutDetailsThunk =
	(payload: {
		_id: string | ObjectId;
		description: string;
		firstname: string;
		lastname: string;
		// phone_number: string;
		// country_code: string;
		email: string;
		date_of_birth: string;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateUserAboutDetails(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/update-about-details", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateUserDetailThunk =
	(payload: {
		_id: string | ObjectId;
		email: string;
		sessionId: ObjectId | string;
		first_name: string;
		last_name: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateUserDetail(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/update-user-details", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateUserSkillsThunk =
	(payload: { _id: string; skills: Array<string> }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateUserSkills(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/update-skills", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const AddUserCoursesThunk =
	(payload: { _id: string; courses: Array<string>; sessionId: ObjectId | string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.AddUserCourses(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/add-courses", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const AddUserLanguageThunk =
	(payload: { _id: string; language: Array<string>; sessionId: ObjectId | string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.AddUserLanguage(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/add-languages", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const AddExperienceForUserThunk =
	(payload: {
		_id: string;
		designation: string;
		joined_at: string;
		organization: string;
		left_at: string;
		request_status: string;
		request_date: string;
		accepted_or_rejected_by: string;
		accepted_or_rejected_date: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.addExperienceForUser(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/add-experience", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const AddHonorsAndAwardsDetailsForUserThunk =
	(payload: {
		_id: string;
		title: string;
		description: string;
		issued_by: string;
		issued_at: string;
		media_url: string;
		mime_type: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.AddHonorsAndAwardsDetailsForUser(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/add-honor-awards", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const AddLicenseAndCertificationDetailsForUserThunk =
	(payload: {
		_id: string;
		title: string;
		description: string;
		issued_by: string;
		issued_at: string;
		media_url: string;
		mime_type: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.AddLicenseAndCertificationDetailsForUser(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/add-licenses-certifications", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const addSaveEntityBusinessProfileThunk =
	(payload: {
		is_business_profile: boolean;
		user_id: ObjectId | string;
		business_profile_id: ObjectId | string;
		entity_id: ObjectId | string;
		entity_type: string;
		add_remove: string;
		url: string;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.AddSaveEntityBusinessProfile(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/add-saved-entity", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const addSaveEntityUserProfileThunk =
	(payload: {
		user_id: string;
		entity_id: string | ObjectId;
		entity_type: string;
		add_remove: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.AddSaveEntityUserProfile(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/add-saved-entity", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateServiceAreasOfOperationThunk =
	(payload: {
		service_id: ObjectId | string;
		areas_of_operation:
			| Array<{
					title: string;
					pincode: string;
					location_coordinates: Array<number>;
			  }>
			| [];
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateServiceAreasOfOperation(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/services/edit-services/update-areas-of-operation", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateServiceOverViewThunk =
	(payload: {
		service_id: ObjectId | string;
		overview: string;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateServiceOverView(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/services/edit-services/update-service-overview", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateServiceDetailsThunk =
	(payload: {
		service_id: ObjectId | string;
		service_category: string;
		service_type: Array<string>;
		service_subtype: Array<string>;
		other_service_subtype: string;
		tags: Array<string>;
		min_price: number;
		max_price: number;
		currency: string;
		show_price: boolean;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateServiceDetails(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/services/edit-services/update-service-details", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateServiceSpecificationThunk =
	(payload: {
		service_id: ObjectId | string;
		service_specifications: Array<string>;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateServiceSpecification(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/services/edit-services/update-service-specifications", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateServiceProviderInfoThunk =
	(payload: {
		service_id: ObjectId | string;
		service_provider_name: string;
		service_provider_year_established: number;
		service_provider_description: string;
		service_provider_upcoming_projects: number;
		service_provider_completed_projects: number;
		service_provider_ongoing_projects: number;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateServiceProvider(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/services/edit-services/update-service-provider-info", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const followUserThunk =
	(payload: {
		follower_is_business_profile: boolean;
		follower_user_id: ObjectId | string;
		follower_business_profile_id: ObjectId | string;
		following_is_business_profile: boolean;
		following_user_id: ObjectId | string;
		following_business_profile_id: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.followUser(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/follow-user", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const unfollowUserThunk =
	(payload: {
		follower_is_business_profile: boolean;
		follower_user_id: ObjectId | string;
		follower_business_profile_id: ObjectId | string;
		following_is_business_profile: boolean;
		following_user_id: ObjectId | string;
		following_business_profile_id: ObjectId | string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.unfollowUser(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/unfollow-user", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdatePropertyDetailsThunk =
	(payload: {
		sessionId: string | ObjectId;
		property_id: string | ObjectId;
		search_string: string;
		delimited_search_string: string;
		transaction_type: string;
		property_category: string;
		property_type: Array<string>;
		property_subtype: string;
		min_price: string | number;
		max_price: string | number;
		currency: "INR" | "AED";
		security_deposit: number;
		possession_date: Date | string;
		property_age: number;
		availability: Array<string>;
		unit_facing: Array<string>;
		land_facing: Array<string>;
		tag: Array<string>;
		furnishing: Array<string> | [];
		min_bedrooms: string | number;
		max_bedrooms: string | number;
		min_bathrooms: string | number;
		max_bathrooms: string | number;
		min_balconies: string | number;
		max_balconies: string | number;
		total_floors: string | number;
		floor_no: string | number;
		no_of_seats: string | number;
		no_of_units: string | number;
		land_area: number;
		land_area_unit: string;
		min_super_built_up_area: string | number;
		max_super_built_up_area: string | number;
		super_built_up_area_unit: string;
		min_carpet_area: string | number;
		max_carpet_area: string | number;
		carpet_area_unit: string;
		plot_dimensions: Array<string> | string;
		other_plot_dimensions: string[];
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdatePropertyDetails(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/properties/edit-property/update-property-details", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateConfigAndAreaThunk =
	(payload: {
		sessionId: string | ObjectId;
		property_id: string | ObjectId;
		search_string: string;
		delimited_search_string: string;
		min_price: string | number;
		max_price: string | number;
		currency: "INR" | "AED";
		land_area: number;
		land_area_unit: string;
		min_super_built_up_area: string | number;
		max_super_built_up_area: string | number;
		super_built_up_area_unit: string;
		min_carpet_area: string | number;
		max_carpet_area: string | number;
		carpet_area_unit: string;
		plot_dimensions: Array<string> | string;
		other_plot_dimensions: string[];
		property_type: Array<string>;
		furnishing: Array<string>;
		min_bedrooms: string | number;
		max_bedrooms: string | number;
		min_bathrooms: string | number;
		max_bathrooms: string | number;
		min_balconies: string | number;
		max_balconies: string | number;
		total_floors: string | number;
		floor_no: string | number;
		no_of_seats: string | number;
		no_of_units: string | number;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateConfigAndArea(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/properties/edit-property/update-config-and-area", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdatePropertyRegulatoryInfoThunk =
	(payload: {
		sessionId: string | ObjectId;
		property_id: string | ObjectId;
		rera_number?: string;
		regulatory_permit_number?: string;
		broker_license_number?: string;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdatePropertyRegulatoryInfo(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/properties/edit-property/update-regulatory-info", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateAboutDeveloperThunk =
	(payload: {
		sessionId: string | ObjectId;
		developer_name: string;
		developer_slug: string;
		description: string;
		year_est: number;
		phoneNumber: string;
		countryCode: string;
		delimited_search_string: string;
		search_string: string;
		property_id: string | ObjectId;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateAboutDeveloperProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/properties/edit-property/update-about-developer", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const UpdateAboutLocationPropertyThunk =
	(payload: {
		sessionId: string | ObjectId;
		about_location: Array<string>;
		property_id: string | ObjectId;
		editor_is_business_profile: boolean;
		phoneNumber: string;
		countryCode: string;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateAboutLocationProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/properties/edit-property/update-about-location", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const UpdateAboutPropertyThunk =
	(payload: {
		sessionId: string | ObjectId;
		about_property: Array<string>;
		property_id: string | ObjectId;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateAboutProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/properties/edit-property/update-about-property", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateAmenitiesThunk =
	(payload: {
		sessionId: string | ObjectId;
		amenities: Array<{ id: string; label: string; icon: string }> | [];
		property_id: string | ObjectId;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateAmenities(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/properties/edit-property/update-amenities", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateOverviewThunk =
	(payload: {
		sessionId: string | ObjectId;
		overview: string;
		manual_overview: string;
		delimited_search_string: string;
		search_string: string;
		property_id: ObjectId | string;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateOverView(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/properties/edit-property/update-overview", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateAiOverviewThunk =
	(payload: {
		sessionId: string | ObjectId;
		overview: string;
		delimited_search_string: string;
		search_string: string;
		property_id: ObjectId | string;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		manual_overview: string;
		phoneNumber: string;
		countryCode: string;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateAiOverView(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/properties/edit-property/update-ai-overview", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateVicinityThunk =
	(payload: {
		sessionId: string | ObjectId;
		property_id: string | ObjectId;
		vicinity: Array<{}>;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateVicinity(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/properties/edit-property/update-vicinity", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setCreateBoostThunk =
	(payload: StateInterface["boost"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setCreateBoost(payload));
	};

export const setStartBoostThunk =
	(payload: {
		boosted_entity_type: string;
		boosted_entity_id: string;
		boosted_by_is_business_profile: boolean;
		boosted_by_business_profile_id: string;
		boosted_by_user_id: string;
		boost_location: Array<{}>;
		boost_start_date: [Date | string, Date | string];
		boost_end_date: [Date | string, Date | string];
		beegru_points_cost: number;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.setStartBoost(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/boosting/create-new-boost", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const EmployeeRequestThunk =
	(payload: {
		employee_is_business_profile: boolean;
		employee_business_profile_id: string;
		employee_user_id: string;
		business_profile_id: ObjectId | string;
		editor_user_id: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.EmployeeRequest(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/new-employee-request", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const acceptEmployeeRequestThunk =
	(payload: { employee_request_id: string; business_profile_id: string; editor_user_id: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.acceptEmployeeRequest(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/accept-employee-request", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const deleteEmployeeRequestThunk =
	(payload: { employee_user_id: string; business_profile_id: string; editor_user_id: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.deleteEmployeeRequest(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/remove-employee", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateBoostThunk =
	(payload: {
		boosted_entity_type: string;
		boosted_entity_id: string;
		boosted_by_is_business_profile: boolean;
		boosted_by_business_profile_id: string;
		boosted_by_user_id: string;
		boost_location: Array<{}>;
		boost_start_date: [Date | string, Date | string];
		boost_end_date: [Date | string, Date | string];
		beegru_points_cost: number;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateBoost(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/boosting/update-boost", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const cancelBoostThunk =
	(payload: {
		boosted_entity_type: string;
		boosted_entity_id: string;
		boosted_by_is_business_profile: boolean;
		boosted_by_business_profile_id: string;
		boosted_by_user_id: string;
		boost_location: Array<{}>;
		boost_start_date: [Date | string, Date | string];
		boost_end_date: [Date | string, Date | string];
		beegru_points_cost: number;
		boost_id: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.cancelBoost(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/boosting/cancel-boost", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const SetDefaultLookingForThunk =
	(payload: StateInterface["looking_for"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.SetDefaultLookingFor(payload));
	};

export const SetDefaultLocationBoostThunk =
	(payload: StateInterface["location_boost"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.SetDefaultLocationBoost(payload));
	};

export const SetDefaultLocationThunk =
	(payload: StateInterface["location"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.SetDefaultLocation(payload));
	};

export const fetchReceiptPageThunk =
	(payload: StateInterface["receipt_page"]): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchReceiptPageData(
				await (await fetch(process.env.PRODUCTION_API_URL + "receipt-data/order_" + payload)).json(),
			),
		);
	};

export const fetchAccountSettingsPageThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAccountSettingsPageData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/" + slug, {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchAccountSettingsProfileKycInfoThunk =
	(payload: { profile_to_verify_id: ObjectId | string; profile_to_verify_type: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAccountSettingsProfileKycInfo(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/fetch-profile-kyc-info", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const addPropertyImagesThunk =
	(payload: {
		property_id: ObjectId | string;
		file_id: Array<ObjectId | null>;
		priorityIndex: number;
		isPrioritySelected: boolean;
		category: Array<string>;
		mime_type: Array<string>;
		sessionId: string | ObjectId;
		phoneNumber: string;
		countryCode: string;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		profile_context: profile_contextInterface;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.addPropertyImages(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/upload-property-images", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const CreateNewPropertyThunk =
	(payload: {
		slug: string;
		title: string;
		creator_is_business_profile: boolean;
		creator_user_id: string | ObjectId;
		creator_business_profile_id: string | ObjectId;
		// new_property_data: StateInterface["new_property_data"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.createNewProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/create-new-property", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const createNewPropertyWithUploadToolThunk =
	(payload: {
		createNewBusinessProfile: Boolean;
		businessProfileId: string | ObjectId;
		creatorIsBusinessProfile: boolean;
		businessProfileName: string;
		businessProfileType: string;
		userId: string | ObjectId;
		phoneNumber: string;
		countryCode: string;
		propertyName: string;
		propertyLocationName: string;
		propertyLocationCoordinates: Array<number>;
		propertyCountry: string;
		propertyStateOrProvince: string;
		propertyCity: string;
		propertyTransactionType: string;
		propertyCategory: string;
		propertyType: Array<string>;
		propertySubtype: string;
		propertyMinPrice: number;
		propertyMaxPrice: number;
		propertyCurrency: string;
		propertyRegulatoryPermit: {
			permit_number: string;
			issuing_authority: string;
		};
		brokerLicense: {
			permit_number: string;
			permit_name: string;
		};
		propertySecurityDeposit: number;
		propertyStatus: string;
		propertyPossessionDate: string | Date;
		propertyAge: number;
		propertyFacing: Array<string>;
		propertyFurnishing: Array<string>;
		propertyMinBedrooms: number;
		propertyMaxBedrooms: number;
		propertyMinBathrooms: number;
		propertyMaxBathroom: number;
		propertyNoOfBalconies: number;
		propertyNoOfSeats: number;
		propertyNoOfUnits: number;
		propertyNoOfFloors: number;
		propertyPlotDimensions: Array<string>;
		propertyLandAreaUnit: string;
		propertyLandArea: number;
		propertySuperBuiltUpAreaUnit: string;
		propertyMinSuperBuiltUpArea: number;
		propertyMaxSuperBuiltUpArea: number;
		propertyIsCarpetAreaPercentage: true;
		propertyCarpetAreaPercentage: string;
		propertyCarpetAreaUnit: string;
		propertyMinCarpetArea: number;
		propertyMaxCarpetArea: number;
		propertyImages: Array<{
			file_id: ObjectId | string;
			mime_type: string;
			category: string;
			caption: string;
			priority: number;
		}>;
		propertyOverview: string;
		sessionId: string;
		locale: string;
		/* The draft this listing finishes (Continue editing); the route posts it in place. */
		draftPropertyId?: string;
	}): AppThunk<Promise<{ ok: boolean }>> =>
	async (dispatch) => {
		/* Answers whether a listing was created, so the wizard can stop its spinner and show the error
		   on a failure (the reducer leaves state alone then). */
		let result: any = null;
		try {
			result = await (
				await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/create-new-property-with-upload-tools", {
					method: "POST",
					cache: "no-store",
					body: JSON.stringify(payload),
				})
			).json();
		} catch {
			result = { status: "error", payload: null, missingFields: null, error: "Network error" };
		}
		dispatch(globalSlice.actions.createNewPropertyWithUploadTool(result));
		const created = result?.payload?.propertyData;
		return { ok: result?.status === "success" && Array.isArray(created) && created.length > 0 };
	};

export const setPreviousPropertyDataThunk =
	(payload: StateInterface["new_property_data"] | null): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setPreviousPropertyData(payload));
	};

export const setPreviousServiceDataThunk =
	(payload: StateInterface["new_service_data"] | null): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setPreviousServiceData(payload));
	};

export const setPostPropertyWithUploadToolSnackbarErrorThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setPostPropertyWithUploadToolSnackbarError(payload));
	};

export const CreateNewServiceThunk =
	(payload: {
		slug: string;
		title: string;
		creator_is_business_profile: boolean;
		creator_user_id: string | ObjectId;
		creator_business_profile_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CreateNewService(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/create-new-service", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const CreateNewServiceWithUploadToolThunk =
	(payload: {
		sessionId: ObjectId | string;
		createNewBusinessProfile: Boolean;
		businessProfileId: string | ObjectId;
		businessProfileName: string;
		businessProfileType: string;
		userId: string | ObjectId;
		phoneNumber: string;
		countryCode: string;
		serviceName: string;
		serviceLocationName: string;
		serviceLocationCoordinates: Array<number>;
		servicProviderName: string;
		serviceType: Array<string>;
		serviceSubType: Array<string>;
		serviceMinPrice: number;
		serviceMaxPrice: number;
		serviceImages: service_images[];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.createNewServiceWithUploadTool(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/edit-services/create-new-service-with-upload-tools", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdatePropertyImageThunk =
	(payload: {
		property_id: ObjectId | string;
		file_id: Array<ObjectId | string>;
		prev_id: Array<ObjectId | string>;
		mime_type: Array<string | undefined>;
		phoneNumber: string;
		countryCode: string;
		caption: string;
		category: Array<string>;
		priority: number;
		sessionId: string;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updatePropertyImage(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/update-images", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const CropPropertyImageThunk =
	(payload: {
		property_id: ObjectId | string;
		file_id: ObjectId | string;
		prev_id: ObjectId | string;
		mime_type: string | undefined;
		sessionId: ObjectId | string;
		phoneNumber: string;
		countryCode: string;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.cropPropertyImage(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/crop-image", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const deletePropertyImageThunk =
	(payload: {
		property_id: ObjectId | string;
		file_id: ObjectId | string;
		sessionId: string;
		phoneNumber: string;
		countryCode: string;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		profile_context: profile_contextInterface;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.deletePropertyImage(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/delete-property-image", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const deleteServiceImageThunk =
	(payload: {
		service_id: ObjectId | string;
		file_id: ObjectId | string;
		sessionId: ObjectId | string;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.deleteServiceImage(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/edit-services/delete-service-image", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const deleteBusinessProfilePictureThunk =
	(payload: {
		business_profile_id: ObjectId | string;
		file_id: ObjectId | string;
		sessionId: ObjectId | string;
		url: string;
		editor_is_business_profile: boolean;
		editor_business_profile_id: ObjectId | string;
		editor_user_id: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.deleteBusinessProfilePicture(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/delete-profile-picture", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const deleteUserProfilePictureThunk =
	(payload: {
		user_id: ObjectId | string;
		file_id: ObjectId | string;
		sessionId: ObjectId | string;
		url: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.deleteUserProfilePicture(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/delete-profile-picture", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const deleteUserCoverImageThunk =
	(payload: {
		user_id: ObjectId | string;
		file_id: ObjectId | string;
		sessionId: ObjectId | string;
		url: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.deleteUserCoverImage(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/delete-cover-image", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const CreateNewSubscriptionThunk =
	(payload: {
		plan_slug: string;
		subscribed_user_id: string | ObjectId;
		subscribed_entity_id: string | ObjectId;
		subscribed_entity_type: string;
		sessionId: ObjectId | string;
		locale: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CreateNewSubscription(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "subscriptions/create-new-subscription", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const RenewSubscriptionThunk =
	(payload: {
		plan_slug: string;
		subscription_id: ObjectId | string;
		subscribed_user_id: string | ObjectId;
		subscribed_entity_id: string | ObjectId;
		subscribed_entity_type: string;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.RenewSubscription(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "subscriptions/renew-subscription", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const CancelSubscriptionThunk =
	(payload: {
		subscription_id: ObjectId | string;
		subscribed_user_id: string | ObjectId;
		subscribed_entity_id: string | ObjectId;
		subscribed_entity_type: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CancelSubscription(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "subscriptions/cancel-subscription", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const BusinessDashboardCreateNewSubscriptionThunk =
	(payload: {
		plan_slug: string;
		subscribed_user_id: string | ObjectId;
		subscribed_entity_id: string | ObjectId;
		subscribed_entity_type: string;
		sessionId: ObjectId | string;
		locale: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.BusinessDashboardCreateNewSubscription(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/settings/create-new-subscription", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const BusinessDashboardRenewSubscriptionThunk =
	(payload: {
		plan_slug: string;
		subscription_id: ObjectId | string;
		subscribed_user_id: string | ObjectId;
		subscribed_entity_id: string | ObjectId;
		subscribed_entity_type: string;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.BusinessDashboardRenewSubscription(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/settings/renew-subscription", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const BusinessDashboardCancelSubscriptionThunk =
	(payload: {
		subscription_id: ObjectId | string;
		subscribed_user_id: string | ObjectId;
		subscribed_entity_id: string | ObjectId;
		subscribed_entity_type: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.BusinessDashboardCancelSubscription(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/settings/cancel-subscription", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const ProfileToUpgradeThunk =
	(payload: StateInterface["profile_to_upgrade"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.ProfileToUpgrade(payload));
	};

export const ProfileToUpgradeIdThunk =
	(payload: StateInterface["profile_to_upgrade_id"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.ProfileToUpgradeId(payload));
	};

export const ProfileToUpgradeTypeThunk =
	(payload: StateInterface["profile_to_upgrade_type"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.ProfileToUpgradeType(payload));
	};

export const deletePropertyDetailsThunk =
	(payload: {
		property_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		sessionId: string;
		url: string;
		locale: string;
		profile_context: profile_contextInterface;
	}): AppThunk<Promise<{ ok: boolean; code: string }>> =>
	async (dispatch) => {
		/* Answers whether the listing was deleted, and the refusal's code ("broadcast_in_review"), so the dialog can
		   say why; the reducer runs only on a success, since it reads the remaining listings. */
		let result: any = null;
		try {
			result = await (
				await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/delete-property", {
					method: "POST",
					cache: "no-store",
					body: JSON.stringify(payload),
				})
			).json();
		} catch {
			result = { status: "error", payload: null, missingFields: null, error: "Network error" };
		}
		if (result?.status === "success") dispatch(globalSlice.actions.deletePropertyDetails(result));
		return { ok: result?.status === "success", code: String(result?.payload?.code || "") };
	};

export const addServiceImagesThunk =
	(payload: {
		service_id: ObjectId | string;
		file_id: Array<ObjectId | null>;
		priorityIndex: number;
		isPrioritySelected: boolean;
		mime_type: Array<string>;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.addServiceImages(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/edit-services/upload-service-images", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateServiceImageThunk =
	(payload: {
		service_id: ObjectId | string;
		file_id: Object | null;
		prev_id: any;
		mime_type: string | undefined;
		caption: string;
		priority: number;
		sessionId: ObjectId | string;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateServiceImage(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/edit-services/update-images", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const CropServiceImageThunk =
	(payload: {
		service_id: ObjectId | string;
		file_id: ObjectId | string;
		prev_id: ObjectId | string;
		mime_type: string | undefined;
		sessionId: ObjectId | string;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.cropServiceImage(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/edit-services/crop-image", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updatePersonalInfoThunk =
	(payload: {
		user_id: string | ObjectId;
		first_name: string;
		last_name: string;
		company_name: string;
		company_slug: string;
		occupation: string;
		email: string;
		address_line1: string;
		address_line2: string;
		city: string;
		state: string;
		country: string;
		pincode: string;
		profile_picture_file_id: ObjectId | string;
		sessionId: string | ObjectId;
		url: string;
		locale: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updatePersonalInfo(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/update-personal-info", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateUserKycInfoThunk =
	(payload: {
		sessionId: ObjectId | string;
		profile_id: ObjectId | string;
		profile_type: string;
		kyc_type: string;
		pan_number: string;
		selected_document: string;
		pan_frontimage_file_id: ObjectId | string | null;
		aadhaar_number: string;
		aadhaar_frontimage_file_id: ObjectId | string | null;
		aadhaar_backimage_file_id: ObjectId | string | null;
		voter_id_number: string;
		voter_id_frontimage_file_id: ObjectId | string | null;
		voter_id_backimage_file_id: ObjectId | string | null;
		driving_licence_number: string;
		driving_licence_frontimage_file_id: ObjectId | string | null;
		driving_licence_backimage_file_id: ObjectId | string | null;
		passport_number: string;
		passport_frontimage_file_id: ObjectId | string | null;
		passport_backimage_file_id: ObjectId | string | null;
		url: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateUserKycInfo(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/update-user-kyc-info", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateBusinessProfileKycInfoThunk =
	(payload: {
		sessionId: ObjectId | string;
		profile_id: ObjectId | string;
		profile_type: string;
		kyc_type: string;
		pan_number: string;
		selected_document: string;
		pan_frontimage_file_id: ObjectId | string | null;
		aadhaar_number: string;
		aadhaar_frontimage_file_id: ObjectId | string | null;
		aadhaar_backimage_file_id: ObjectId | string | null;
		voter_id_number: string;
		voter_id_frontimage_file_id: ObjectId | string | null;
		voter_id_backimage_file_id: ObjectId | string | null;
		driving_licence_number: string;
		driving_licence_frontimage_file_id: ObjectId | string | null;
		driving_licence_backimage_file_id: ObjectId | string | null;
		passport_number: string;
		passport_frontimage_file_id: ObjectId | string | null;
		passport_backimage_file_id: ObjectId | string | null | undefined;
		tin_number: string | undefined;
		tin_certificate_frontimage_file_id: ObjectId | string | null | undefined;
		gst_number: string | undefined;
		gst_certificate_frontimage_file_id: ObjectId | string | null | undefined;
		legal_licence_number: string | undefined;
		legal_licence_frontimage_file_id: ObjectId | string | null | undefined;
		moa_number: string | undefined;
		moa_frontimage_file_id: ObjectId | string | null | undefined;
		rera_number: string | undefined;
		rera_certificate_frontimage_file_id: ObjectId | string | null | undefined;
		partnership_deed_number: string | undefined;
		partnership_deed_frontimage_file_id: ObjectId | string | null | undefined;
		partnership_reg_certificate_number: string | undefined;
		partnership_reg_certificate_frontimage_file_id: ObjectId | string | null | undefined;
		aoa_number: string | undefined;
		aoa_frontimage_file_id: ObjectId | string | null | undefined;
		coi_number: string | undefined;
		coi_frontimage_file_id: ObjectId | string | null | undefined;
		llp_agreement_number: string | undefined;
		llp_agreement_frontimage_file_id: ObjectId | string | null | undefined;
		url: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateBusinessProfileKycInfo(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/update-business-profile-kyc-info", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateLocationPropertyThunk =
	(payload: {
		sessionId: string | ObjectId;
		property_id: string | ObjectId;
		location_title: string;
		pincode: string;
		propertyCountry: string;
		propertyStateOrProvince: string;
		propertyCity: string;
		location_coordinates: Array<number>;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateLocationProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/update-location", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateServiceLocationThunk =
	(payload: {
		service_id: string | ObjectId;
		location_title: string;
		pincode: string;
		location_coordinates: Array<number>;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		url: string;
		phoneNumber: string;
		countryCode: string;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateServiceLocation(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/edit-services/update-location", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const addProfilePictureThunk =
	(payload: {
		value: string;
		user_id: ObjectId | string;
		profile_picture_file_id: ObjectId | string;
		mime_type: string;
		sessionId: ObjectId | string;
		url: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.addProfilePicture(
				(
					await (
						await fetch(process.env.PRODUCTION_API_URL + "account-settings/add-profile-picture", {
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						})
					).json()
				).payload,
			),
		);
	};

export const addCoverImageThunk =
	(payload: {
		value: string;
		user_id: ObjectId | string;
		cover_image_file_id: ObjectId | string;
		mime_type: string | undefined;
		sessionId: ObjectId | string;
		url: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.addCoverImage(
				(
					await (
						await fetch(process.env.PRODUCTION_API_URL + "account-settings/add-cover-image", {
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						})
					).json()
				).payload,
			),
		);
	};

export const addBusinessProfilePictureThunk =
	(payload: {
		value: string;
		business_profile_id: ObjectId | string;
		profile_picture_file_id: ObjectId | string;
		mime_type: string | undefined;
		sessionId: ObjectId | string;
		url: string;
		editor_is_business_profile: boolean;
		editor_business_profile_id: ObjectId | string;
		editor_user_id: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.addBusinessProfilePicture(
				(
					await (
						await fetch(process.env.PRODUCTION_API_URL + "business-profiles/add-profile-picture", {
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						})
					).json()
				).payload,
			),
		);
	};

export const createNewBusinessProfileThunk =
	(payload: {
		slug: string;
		title: string;
		business_profile_type: string;
		creator_user_id: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.createNewBusinessProfile(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/create-new-business-profile", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const createNewOFBPropertyThunk =
	(payload: {
		slug: string;
		title: string;
		creator_is_business_profile: boolean | undefined;
		creator_user_id: string | ObjectId;
		creator_business_profile_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.createNewOFBProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/create-new-ofb-property", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const deletePropertyOFBDetailsThunk =
	(payload: {
		property_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		sessionId: string;
		url: string;
	}): AppThunk<Promise<{ ok: boolean; code: string }>> =>
	async (dispatch) => {
		/* Answers like deletePropertyDetailsThunk: whether it was deleted and the refusal's code. */
		let result: any = null;
		try {
			result = await (
				await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/delete-ofb-property", {
					method: "POST",
					cache: "no-store",
					body: JSON.stringify(payload),
				})
			).json();
		} catch {
			result = { status: "error", payload: null, missingFields: null, error: "Network error" };
		}
		if (result?.status === "success") dispatch(globalSlice.actions.deletePropertyOFBDetails(result));
		return { ok: result?.status === "success", code: String(result?.payload?.code || "") };
	};

export const deletePropertyOFBUserThunk =
	(payload: {
		property_id: string;
		is_business_profile: boolean;
		user_id: string;
		business_profile_id: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.deletePropertyOFBUsers(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/delete-ofb-property", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const createNewOFBPropertyUserThunk =
	(payload: {
		slug: string;
		title: string;
		creator_is_business_profile: boolean;
		creator_user_id: string | ObjectId;
		creator_business_profile_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.createNewOFBPropertyUser(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/create-new-ofb-property", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const updateBoomsThunk =
	(payload: {
		post_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		created_at: string;
		url: string;
		tabValue: number;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateBooms({
				tabValue: payload.tabValue,
				response: await (
					await fetch(process.env.PRODUCTION_API_URL + "posts/add-booms", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			}),
		);
	};
export const RemoveBoomsThunk =
	(payload: {
		post_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		created_at: string;
		url: string;
		tabValue: number;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.RemoveBooms({
				tabValue: payload.tabValue,
				response: await (
					await fetch(process.env.PRODUCTION_API_URL + "posts/remove-booms", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			}),
		);
	};
export const updateBlastsThunk =
	(payload: {
		post_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		created_at: string;
		url: string;
		tabValue: number;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateBlasts({
				tabValue: payload.tabValue,
				response: await (
					await fetch(process.env.PRODUCTION_API_URL + "posts/add-blasts", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			}),
		);
	};

export const CreateNewPostUserThunk =
	(payload: {
		creator_is_business_profile: boolean;
		creator_user_id: string | ObjectId;
		creator_business_profile_id: string | ObjectId;
		body: string;
		file_id: Array<ObjectId | null>;
		privacy_status: string;
		search_string: string;
		search_points: number;
		is_blasted: boolean;
		blasted_post: ObjectId | string;
		type: string;
		sessionId: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CreateNewPostUser({
				type: payload.type,
				response: await (
					await fetch(process.env.PRODUCTION_API_URL + "posts/create-new-post", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			}),
		);
	};

export const CreateNewCommentsUserPostThunk =
	(payload: {
		post_id: string | ObjectId;
		creator_is_business_profile: boolean;
		creator_user_id: string | ObjectId;
		creator_business_profile_id: string | ObjectId;
		name: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CreateNewCommentsUserPost(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "posts/comments/add-comment", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const deletePostUserThunk =
	(payload: {
		post_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		url: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.deletePostUser(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "posts/delete-post", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const ReplyToCommentThunk =
	(payload: {
		body: string;
		deleted: boolean;
		reported: boolean;
		comment_id: string | ObjectId;
		post_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		url: string;
		tabValue: number;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.ReplyToComment({
				tabValue: payload.tabValue,
				response: await (
					await fetch(process.env.PRODUCTION_API_URL + "posts/comments/add-comment-reply", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			}),
		);
	};

export const deleteServiceDetailsThunk =
	(payload: {
		service_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		sessionId: string | ObjectId;
		url: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.deleteServiceDetails(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/delete-services", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const setProfileContextThunk =
	(payload: {
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		page_type: string;
		user_kyc_status: string;
		user_subscription_information: BeegruUserSubscriptionInformation;
		remember_choice: boolean;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.setProfileContext(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/profile-context", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const setActionForboomsThunk =
	(payload: StateInterface["actionForBooms"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setActionForbooms(payload));
	};

export const profileContextThunk =
	(payload: StateInterface["profile_context"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.profileContext(payload));
	};
export const bookmarkThunk =
	(payload: {
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		is_business_profile: boolean;
		entity_id: string | ObjectId;
		entity_type: string;
		add_remove: string;
		url: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.bookmark(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "posts/add-remove-bookmark", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const reportPostThunk =
	(payload: {
		user_id: string | ObjectId;
		reason: string;
		post_id: string | ObjectId;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.reportPost(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "posts/report-post", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const postSwrForUserThunk =
	(payload: StateInterface["postSwrForUser"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.postSwrForUser(payload));
	};
export const postSwrForBusinessThunk =
	(payload: StateInterface["postSwrForBusiness"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.postSwrForBusiness(payload));
	};

export const commentSwrThunk =
	(payload: StateInterface["comment_swr"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.commentSwr(payload));
	};

export const PublishPropertyThunk =
	(payload: {
		property_id: string | ObjectId;
		status: string;
		creator_is_business_profile: boolean;
		creator_user_id: string | ObjectId;
		creator_business_profile_id: string | ObjectId;
		phoneNumber: string;
		countryCode: string;
		sessionId: string | ObjectId;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.publishproperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/publish-property", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const PublicPrivatePropertyThunk =
	(payload: { property_id: string | ObjectId; is_public: boolean; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.publicproperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/public-private-property", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const PublishServiceThunk =
	(payload: {
		service_id: string | ObjectId;
		status: string;
		creator_is_business_profile: boolean;
		creator_user_id: string | ObjectId;
		creator_business_profile_id: string | ObjectId;
		phoneNumber: string;
		countryCode: string;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.publishservice(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/edit-services/publish-service", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const PublicPrivateServiceThunk =
	(payload: { service_id: string | ObjectId; is_public: boolean; sessionId: ObjectId | string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.publicservice(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/edit-services/public-private-service", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const OnBoardingThunk =
	(payload: { user_id: string | ObjectId; first_name: string; last_name: string; recoveryEmailId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.OnBoarding(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "on-boarding/On-boarding", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateNoOfEmployeesThunk =
	(payload: {
		no_of_employees: number;
		business_profile_id: string | ObjectId;
		url: string;
		creator_user_id: string | ObjectId;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateNofEmployees(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/update-no-of-employees", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const setSavedIsPublicThunk =
	(payload: { business_profile_id: string | ObjectId; saved_is_public: boolean }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.setSavedIsPublic(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/saved_is_public", {
						method: "POST",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSinglePostThunk =
	({ id, sessionId }: { id: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePost(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "posts/" + id, {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const businessProfileListTabViewThunk =
	(payload: StateInterface["business_profile_listings_tab_view"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.businessProfileListTabView(payload));
	};

export const setSearchPropertiesThunk =
	(payload: StateInterface["single_business_profile"]["properties_og"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setSearchProperties(payload));
	};
export const setSearchServiceThunk =
	(payload: StateInterface["single_business_profile"]["services_og"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setSearchServices(payload));
	};
export const setSearchOFBThunk =
	(payload: StateInterface["single_business_profile"]["ofb_properties_og"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setSearchOFBProperties(payload));
	};

export const loadMorePropertiesThunk =
	(payload: { skip: number; sessionId: string; business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreProperties(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/load-more-properties", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreServicesThunk =
	(payload: { skip: number; sessionId: string; business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreServices(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/load-more-services", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreOfbPropertyThunk =
	(payload: { skip: number; sessionId: string; business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreOfbProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/load-more-ofb", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreSavedPropertiesThunk =
	(payload: { skip: number; sessionId: string; business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreSavedProperties(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/load-more-saved-properties", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreSavedOFBThunk =
	(payload: { skip: number; sessionId: string; business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreSavedOFB(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/load-more-saved-ofb", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreUserSavedPropertiesThunk =
	(payload: { skip: number; sessionId: string | ObjectId; user_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreUserSavedProperties(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/load-more-saved-properties", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreUserSavedOFBThunk =
	(payload: { skip: number; sessionId: string | ObjectId; user_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreUserSavedOFB(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/load-more-saved-properties", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreSavedServicesThunk =
	(payload: { skip: number; sessionId: string; business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreSavedServices(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/load-more-saved-services", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreUserSavedServicesThunk =
	(payload: { skip: number; sessionId: string | ObjectId; user_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreUserSavedServices(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/load-more-saved-services", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreSavedPostsThunk =
	(payload: { skip: number; sessionId: string | ObjectId; business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreSavedPosts(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/load-more-saved-posts", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreUserSavedPostsThunk =
	(payload: { skip: number; sessionId: string | ObjectId; user_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreUserSavedPosts(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/load-more-saved-posts", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const searchPropertyListingsThunk =
	(payload: {
		search_term: string;
		sessionId: string;
		business_profile_id: string | ObjectId;
		business_profile_slug: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.searchPropertyListings(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/search-property-listings", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const changeRecievePromotionalEmailsThunk =
	(payload: {
		sessionId: string | ObjectId;
		user_id: ObjectId | string;
		receive_promotional_emails: boolean;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.changeRecievePromotionalEmails(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/update-receive-promotional-emails", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const changeDataTuningThunk =
	(payload: { fine_tuning_check: boolean; property_id: string | ObjectId; sessionId: ObjectId | string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.changeDataTuning(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/overview-data-tuning", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const changeAutoplayVideosThunk =
	(payload: {
		sessionId: string | ObjectId;
		user_id: ObjectId | string;
		autoplay_videos: boolean;
		url: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.changeAutoplayVideos(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/update-autoplay-videos", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const changeDefaultVideoQualityThunk =
	(payload: {
		sessionId: string | ObjectId;
		user_id: ObjectId | string;
		default_video_quality: string;
		url: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.changeDefaultVideoQuality(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/update-default-video-quality", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreUserPropertiesThunk =
	(payload: { skip: number; sessionId: string | ObjectId; user_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreUserProperties(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/load-more-properties", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const searchUserPropertyListingsThunk =
	(payload: { search_term: string; sessionId: string | ObjectId; user_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.searchUserPropertyListings(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/search-property-listings", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const CreateNewBugReportThunk =
	(payload: {
		first_name: string;
		last_name: string;
		user_id: string;
		phone: string;
		status: string;
		country_code: string;
		feedback_type: string;
		description: string;
		image_path: string;
		url: string;
		screenshot_url: ArrayBuffer | null;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CreateNewBugReport(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "feedback/create-new-feedback", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const CreateWebsiteReportThunk =
	(payload: {
		report_type: string;
		description: string;
		is_business_profile: boolean;
		business_profile_id: ObjectId | string;
		user_id: ObjectId | string;
		url: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CreateNewReport(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "website-reports/create-new-feedback", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setHomeLocationThunk =
	(payload: StateInterface["home_location"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setHomeLocation(payload));
	};

export const fetchHomePageHotPropertiesThunk =
	({
		sessionId,
		// looking_for,
		// location,
		transaction_type,
	}: {
		sessionId: string;
		// looking_for?: any;
		// location?: any;
		transaction_type: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchHomePageHotPropertiesData(
				await //change to hot properties
				(
					await fetch(process.env.PRODUCTION_API_URL + "home/hot-properties", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({
							sessionId,
							// looking_for,
							// location,
							transaction_type,
						}),
					})
				).json(),
			),
		);
	};

export const fetchHomePageHotPropertiesBuyLocalStorageThunk =
	(properties: Array<any>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.fetchHomePageHotPropertiesBuyLocalStorageData(properties));
	};

export const fetchHomePageHotPropertiesRentLocalStorageThunk =
	(properties: Array<any>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.fetchHomePageHotPropertiesRentLocalStorageData(properties));
	};

export const fetchHomePageHotPropertiesOfbLocalStorageThunk =
	(properties: Array<any>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.fetchHomePageHotPropertiesOfbLocalStorageData(properties));
	};

export const fetchHomePageFeaturedPropertiesThunk =
	({
		sessionId,
		// looking_for,
		// location,
		transaction_type,
	}: {
		sessionId: string;
		// looking_for?: any;
		// location?: any;
		transaction_type: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchHomePageFeaturedPropertiesData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "home/featured-properties", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({
							sessionId,
							// looking_for,
							// location,
							transaction_type,
						}),
					})
				).json(),
			),
		);
	};

export const fetchHomePageFeaturedPlotsThunk = (): AppThunk => async (dispatch) => {
	dispatch(
		globalSlice.actions.fetchHomePageFeaturedPlotsData(
			await (
				await fetch(process.env.PRODUCTION_API_URL + "home/featured-plots", {
					method: "POST",
					headers: {
						"Content-Type": "text/plain",
					},
				})
			).json(),
		),
	);
};

export const fetchHomeTopVillasPageThunk = (): AppThunk => async (dispatch) => {
	dispatch(
		globalSlice.actions.fetchHomePageTopVillasData(
			await (
				await fetch(process.env.PRODUCTION_API_URL + "home/top-villas", {
					method: "POST",
					headers: {
						"Content-Type": "text/plain",
					},
				})
			).json(),
		),
	);
};

export const fetchHomePageTopServicesThunk =
	({ sessionId, location }: { sessionId: string; location?: any }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchHomePageTopServicesData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "home/top-services", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId, location }),
					})
				).json(),
			),
		);
	};
// export const fetchHomePageTrendingServicesThunk =
// 	({ sessionId, looking_for, location }: { sessionId: string; looking_for?: any; location?: any }): AppThunk =>
// 	async (dispatch) => {
// 		dispatch(
// 			globalSlice.actions.fetchHomePageTrendingServicesData(
// 				await (
// 					await fetch(process.env.PRODUCTION_API_URL + "home/trending-services", {
// 						method: "POST",
// 						headers: {
// 							"Content-Type": "text/plain",
// 						},
// 						body: JSON.stringify({ sessionId, looking_for, location }),
// 					})
// 				).json(),
// 			),
// 		);
// 	};

export const fetchHomePageTopUsersThunk =
	({ sessionId, looking_for, location }: { sessionId: string; looking_for?: any; location?: any }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchHomePageTopUsersData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "home/top-users", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId, looking_for, location }),
					})
				).json(),
			),
		);
	};
// export const fetchHomePageTrendingUsersThunk =
// 	({ sessionId, looking_for, location }: { sessionId: string; looking_for?: any; location?: any }): AppThunk =>
// 	async (dispatch) => {
// 		dispatch(
// 			globalSlice.actions.fetchHomePageTrendingUsersData(
// 				await (
// 					await fetch(process.env.PRODUCTION_API_URL + "home/trending-users", {
// 						method: "POST",
// 						headers: {
// 							"Content-Type": "text/plain",
// 						},
// 						body: JSON.stringify({ sessionId, looking_for, location }),
// 					})
// 				).json(),
// 			),
// 		);
// 	};
export const fetchHomePageTopBusinessProfilesThunk =
	({
		sessionId,
		looking_for,
		location,
		type,
	}: {
		sessionId: string;
		looking_for?: any;
		location?: any;
		type: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchHomePageTopBusinessProfilesData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "home/top-business-profile", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId, looking_for, location, type }),
					})
				).json(),
			),
		);
	};

export const fetchHomePageTopDevelopersThunk =
	({
		sessionId,
		looking_for,
		location,
		type,
	}: {
		sessionId: string;
		looking_for?: any;
		location?: any;
		type: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchHomePageTopDevelopersData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "home/top-business-profile", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId, looking_for, location, type }),
					})
				).json(),
			),
		);
	};

export const fetchHomePageBeegruTopPicksThunk =
	({
		sessionId,
		// looking_for,
		// location,
		transaction_type,
	}: {
		sessionId: string;
		// looking_for?: any;
		// location?: any;
		transaction_type: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchHomePageBeegruTopPicksData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "home/beegru-top-pick", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({
							sessionId,
							// looking_for,
							// location,
							transaction_type,
						}),
					})
				).json(),
			),
		);
	};

// export const fetchHomePageTrendingBusinessProfilesThunk =
// 	({ sessionId, looking_for, location }: { sessionId: string; looking_for?: any; location?: any }): AppThunk =>
// 	async (dispatch) => {
// 		dispatch(
// 			globalSlice.actions.fetchHomePageTrendingBusinessProfilesData(
// 				await (
// 					await fetch(process.env.PRODUCTION_API_URL + "home/trending-business-profile", {
// 						method: "POST",
// 						headers: {
// 							"Content-Type": "text/plain",
// 						},
// 						body: JSON.stringify({ sessionId, looking_for, location }),
// 					})
// 				).json(),
// 			),
// 		);
// 	};
export const setHomePageBusinessProfileLoadingThunk =
	(loading: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setHomePageBusinessProfileLoading(loading));
	};
export const fetchSingleUserProfileCoverImageAndProfilePictureThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleUserProfileCoverImageAndProfilePicture(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/fetch-cover-image-and-profile-image", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchSingleUserProfileFollowersAndFollowingsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleUserProfileFollowersAndFollowings(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/fetch-followers-and-followings", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchSingleUserProfileAboutDetailsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleUserProfileAboutDetails(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/fetch-about-details", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSingleUserProfilePostsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleUserProfilePosts(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/fetch-user-posts", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const LoadingMoreUserProfilePostsThunk =
	(payload: {
		user_id: string | ObjectId;
		sessionId: string | ObjectId;
		pageNumber: string;
		pageSize: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.LoadingMoreUserProfilePosts(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/load-more-posts", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSingleUserProfileListingsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleUserProfileListings(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/user-property-listing", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchSingleUserProfileReviewsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleUserProfileReviews(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/fetch-users-reviews", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchSingleUserProfileGivenReviewsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleUserProfileGivenReviews(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/fetch-user-given-reviews", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchSingleUserSavedPropertiesThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleUserSavedProperties(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/fetch-users-saved-properties", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSingleUserSavedPostThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleUserSavedPost(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/fetch-users-saved-post", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchSingleUserSavedServiceThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleUserSavedService(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/fetch-users-saved-services", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSingleUserSavedOFBThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleUserSavedOFB(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/fetch-users-saved-ofb", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSingleUserAnalyticsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleUserAnalytics(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/fetch-analytics", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileImagesThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileImages(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-profile-images", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessDashboardProfileDataThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardProfileData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/fetch-profile-data", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessProfileAboutDetailsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileAboutDetails(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-about-details", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileSubscriptionThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileSubscription(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-subscription", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessProfileFollowerAndFollowingThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileFollowerAndFollowing(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-followers-and-followings", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessProfileServiceOfferedThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileServiceOffered(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-service-offered", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileExpertiseThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileExpertise(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-expertise", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileBrokerageThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileBrokerage(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-brokerage", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessProfileFaqsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileFaqs(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-faqs", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileLanguagesThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileLanguages(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-languages", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileLicencesAndCertificatesThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileLicencesAndCertificates(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-licences-and--certificates", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileNoOfEmployeesThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileNoOfEmployees(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-no-of-employees", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileListingPropertyThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileListingProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-listings-property", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileReviewsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileReviews(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-business-profile-reviews", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileListingServiceThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileListingService(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-listings-services", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileListingOFBThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileListingOFB(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-listings-ofb", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileLeadsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileLeads(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-leads", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessProfileSavedPropertyThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileSavedProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-saved-properties", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileSavedServiceThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileSavedService(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-saved-services", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessProfileSavedOFBThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileSavedOFB(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-saved-ofb", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessProfileSavedPostThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileSavedPost(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-saved-posts", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileAnalyticsPropertyThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileAnalyticsProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-properties-analytics", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileAnalyticsServiceThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileAnalyticsService(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-service-analytics", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessProfileAnalyticsPostThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileAnalyticsPost(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-post-analytics", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileAnalyticsOFBsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileAnalyticsOFBs(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-ofbs-analytics", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessProfileAdminsDetailsAndEmployeeRequestThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileAdminsDetailsAndEmployeeRequest(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-admins-details", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchPropertyDataReviewsPageThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchPropertyDataReviewsPageData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + slug + "/fetch-property-data-reviews", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ slug, sessionId }),
					})
				).json(),
			),
		);
	};
export const fetchPropertyUserDataReviewsPageThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchPropertyUserDataReviewsPageData(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-property-user-data-reviews",
						{
							method: "POST",
							headers: {
								"Content-Type": "text/plain",
							},
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};
export const fetchServiceDataReviewsPageThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchServiceDataReviewsPageData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/" + slug + "/fetch-service-data-reviews", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};
export const fetchServiceUserDataReviewsPageThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchServiceUserDataReviewsPageData(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "services/" + payload.slug + "/fetch-service-user-data-reviews",
						{
							method: "POST",
							headers: {
								"Content-Type": "text/plain",
							},
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};
export const fetchSingleServiceImagesThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleServiceImages(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/" + slug + "/fetch-service-images", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchSingleServiceDetailsThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleServiceDetails(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/" + slug + "/fetch-service-details", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchSingleServiceOverviewThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleServiceOverview(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/" + slug + "/fetch-service-overview", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchSingleServiceSpecificationsThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleServiceSpecifications(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/" + slug + "/fetch-service-specifications", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchSingleServiceProviderThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleServiceProvider(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/" + slug + "/fetch-service-provider", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchSingleServiceAreasOfOperationThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleServiceAreasOfOperation(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/" + slug + "/fetch-service-areas-of-operation", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchSingleServiceMapViewThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleServiceMapView(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/" + slug + "/fetch-service-map-view", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchSingleServiceReviewsThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleServiceReviews(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/" + slug + "/fetch-service-reviews", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchSingleServiceCreatorDetailsThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleServiceCreatorDetails(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/" + slug + "/fetch-service-creator-details", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchSingleServiceTopServicesThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleServiceTopServices(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/" + slug + "/fetch-top-services", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const fetchSingleServiceTrendingServicesThunk =
	({ slug, sessionId }: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleServiceTrendingServices(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/" + slug + "/fetch-trending-services", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({ sessionId }),
					})
				).json(),
			),
		);
	};

export const setSingleServiceCategoryThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceCategory(payload));
	};

export const setCreateNewServiceCategoryThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewServiceCategory(payload));
	};

export const setSingleServiceTypeThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceType(payload));
	};

export const setCreateNewServiceTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewServiceType(payload));
	};

export const setSingleServiceSubtypeThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceSubtype(payload));
	};

export const setCreateNewServiceSubTypeThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewServiceSubType(payload));
	};

export const setSingleServiceOtherSubtypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceOtherSubtype(payload));
	};

export const setCreateNewServiceOtherSubTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewServiceOtherSubType(payload));
	};

export const setSingleServiceTagsThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceTags(payload));
	};

export const setSingleServiceShowPriceThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceShowPrice(payload));
	};

export const setCreateNewServiceShowPriceThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewServiceShowPrice(payload));
	};

export const setSingleServiceMinPriceThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceMinPrice(payload));
	};

export const setCreateNewServiceMinPriceThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewServiceMinPrice(payload));
	};

export const setSingleServiceMaxPriceThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceMaxPrice(payload));
	};

export const setCreateNewServiceMaxPriceThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewServiceMaxPrice(payload));
	};

export const setCreateNewServiceUploadImagesThunk =
	(
		payload: Array<{
			file_id: ObjectId | string;
			mime_type: string;
			category: string;
			caption: string;
			priority: number;
			stage: string;
		}>,
	): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewServiceUploadImages(payload));
	};

export const setCreateNewServiceDefaultLocationThunk =
	(payload: { location_coordinates: Array<number>; address: string }): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setCreateNewServiceDefaultLocation(payload));
	};

export const setSingleServiceOverviewThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceOverview(payload));
	};

export const setSingleServiceSpecificationThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceSpecification(payload));
	};

export const setCreateNewServiceProviderNameThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewServiceProviderName(payload));
	};

export const setSingleServiceProviderNameThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceProviderName(payload));
	};

export const setSingleServiceProviderEstablishedYearThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceProviderEstablishedYear(payload));
	};

export const setSingleServiceProviderDescriptionThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceProviderDescription(payload));
	};

export const setSingleServiceAreasOfOperationThunk =
	(
		payload:
			| Array<{
					title: string;
					pincode: string;
					location_coordinates: Array<number>;
			  }>
			| [],
	): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceAreasOfOperation(payload));
	};

export const setSingleServiceLocationTitleThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceLocationTitle(payload));
	};

export const setSingleServiceLocationPincodeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceLocationPincode(payload));
	};

export const setSingleServiceLocationCoordinatesThunk =
	(payload: Array<number>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleServiceLocationCoordinates(payload));
	};

export const setSectionToEditThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSectionToEdit(payload));
	};

export const setDataBeforeEditThunk =
	(payload: any): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setDataBeforeEdit(payload));
	};

export const fetchSinglePropertyDetailsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePropertyDetails(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-property-details", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchDevelopersThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchDevelopers(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-developers", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchNearbyPropertiesThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchNearbyProperties(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-nearby-properties", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSinglePropertyConfigAndAreaThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePropertyConfigAndArea(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-property-config-and-area",
						{
							method: "POST",
							headers: {
								"Content-Type": "text/plain",
							},
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const setSinglePropertyTransactionTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyTransactionType(payload));
	};

export const setCreateNewPropertyTransactionTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyTransactionType(payload));
	};

export const setCreateNewPropertyCurrentPageThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyCurrentPage(payload));
	};

export const setCreateNewServiceCurrentPageThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewServiceCurrentPage(payload));
	};

export const setSinglePropertyCategoryTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyCategoryType(payload));
	};

export const setCreateNewPropertyCategoryTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyCategoryType(payload));
	};

export const setSinglePropertyTypeThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyType(payload));
	};

export const setCreateNewPropertyTypeThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyType(payload));
	};

export const setSinglePropertySubTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertySubType(payload));
	};

export const setCreateNewPropertySubTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertySubType(payload));
	};

export const setSinglePropertyMinPriceThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyMinPrice(payload));
	};

export const setCreateNewPropertyMinPriceThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyMinPrice(payload));
	};

export const setSinglePropertyMaxPriceThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyMaxPrice(payload));
	};

export const setCreateNewPropertyMaxPriceThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyMaxPrice(payload));
	};

export const setSinglePropertySecurityDepositThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertySecurityDeposit(payload));
	};

export const setCreateNewPropertySecurityDepositThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertySecurityDeposit(payload));
	};

export const setCreateNewPropertyCurrencyThunk =
	(payload: "INR" | "AED"): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyCurrency(payload));
	};

export const setSinglePropertyCurrencyThunk =
	(payload: "INR" | "AED"): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyCurrency(payload));
	};

export const setSinglePropertyAvailabiltyThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyAvailabilty(payload));
	};

export const setCreateNewPropertyAvailabiltyThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyAvailabilty(payload));
	};

export const setSinglePropertyPossessionDateThunk =
	(payload: string | Date): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyPossessionDate(payload));
	};

export const setCreateNewePropertyPossessionDateThunk =
	(payload: string | Date): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewePropertyPossessionDate(payload));
	};

export const setSinglePropertyTagsThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyTags(payload));
	};

export const setSinglePropertyUnitFacingThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyUnitFacing(payload));
	};

export const setCreateNewPropertyUnitFacingThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyUnitFacing(payload));
	};

export const setSinglePropertyLandFacingThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyLandFacing(payload));
	};

export const setCreateNewPropertyLandFacingThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyLandFacing(payload));
	};

export const setSinglePropertyAgeThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyAge(payload));
	};

export const setCreateNewPropertyAgeThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyAge(payload));
	};

export const setSinglePropertyFurnishingThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyFurnishing(payload));
	};

export const setCreateNewPropertyFurnishingThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyFurnishing(payload));
	};

export const setSinglePropertyPlotDimensionsThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyPlotDimensions(payload));
	};

export const setCreateNewPropertyPlotDimensionsThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyPlotDimensions(payload));
	};

export const setCreateNewPropertyOverviewThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyOverview(payload));
	};

export const setCreateNewPropertyPermitNumberThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyPermitNumber(payload));
	};

export const setCreateNewPropertyIssuingAuthorityThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyIssuingAuthority(payload));
	};

export const setCreateNewPropertyBrokerLicensePermitNumberThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyBrokerLicensePermitNumber(payload));
	};

export const setCreateNewPropertyBrokerLicensePermitNameThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyBrokerLicensePermitName(payload));
	};

export const setSinglePropertyOtherPlotDimensionsThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyOtherPlotDimensions(payload));
	};

export const setSinglePropertyLandAreaThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyLandArea(payload));
	};

export const setCreateNewPropertyLandAreaThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyLandArea(payload));
	};

export const setCreateNewPropertyLandAreaUnitThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyLandAreaUnit(payload));
	};

export const setSinglePropertyMinSuperBuiltUpAreaThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyMinSuperBuiltUpArea(payload));
	};

export const setCreateNewPropertyMinSuperBuiltUpAreaThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNePropertyMinSuperBuiltUpArea(payload));
	};

export const setSinglePropertyMaxSuperBuiltUpAreaThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyMaxSuperBuiltUpArea(payload));
	};

export const setCreateNewPropertyMaxSuperBuiltUpAreaThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyMaxSuperBuiltUpArea(payload));
	};

export const setCreateNewPropertySuperBuiltUpAreaUnitThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertySuperBuiltUpAreaUnit(payload));
	};

export const setSinglePropertyMinCarpetAreaThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyMinCarpetArea(payload));
	};

export const setCreateNewPropertyMinCarpetAreaThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyMinCarpetArea(payload));
	};

export const setSinglePropertyMaxCarpetAreaThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyMaxCarpetArea(payload));
	};

export const setCreateNewPropertyMaxCarpetAreaThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyMaxCarpetArea(payload));
	};

export const setSinglePropertyMinBedroomsThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyMinBedrooms(payload));
	};

export const setCreateNewPropertyMinBedroomsThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyMinBedrooms(payload));
	};

export const setSinglePropertyMaxBedroomsThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyMaxBedrooms(payload));
	};

export const setCreateNewPropertyMaxBedroomsThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyMaxBedrooms(payload));
	};

export const setSinglePropertyMinBathroomsThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyMinBathrooms(payload));
	};

export const setCreateNewPropertyMinBathroomsThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyMinBathrooms(payload));
	};

export const setSinglePropertyMaxBathroomsThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyMaxBathrooms(payload));
	};

export const setCreateNewPropertyMaxBathroomsThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyMaxBathrooms(payload));
	};

export const setSinglePropertyMinBalconiesThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyMinBalconies(payload));
	};

export const setCreateNePropertyMinBalconiesThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNePropertyMinBalconies(payload));
	};

export const setSinglePropertyMaxBalconiesThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyMaxBalconies(payload));
	};

export const setCreateNewPropertyMaxBalconiesThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyMaxBalconies(payload));
	};

export const setSinglePropertyMaxFloorsThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyMaxFloors(payload));
	};

export const setCreateNewPropertyMaxFloorsThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyMaxFloors(payload));
	};

export const setSinglePropertyFloorNoThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyFloorNo(payload));
	};

export const setCreateNewPropertyFloorNoThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyFloorNo(payload));
	};

export const setSinglePropertyNoOfUnitsThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyNoOfUnits(payload));
	};

export const setCreateNewPropertyNoOfUnitsThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyNoOfUnits(payload));
	};

export const setSinglePropertyNoOfSeatsThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyNoOfSeats(payload));
	};

export const setCreateNewPropertyNoOfSeatsThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyNoOfSeats(payload));
	};

export const setCreatePropertySelectedImagesUrlThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreatePropertySelectedImagesUrl(payload));
	};

export const setCreateNewPropertyUploadImagesThunk =
	(
		payload: Array<{
			file_id: ObjectId | string;
			mime_type: string;
			category: string;
			caption: string;
			priority: number;
			stage: string;
		}>,
	): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyUploadImages(payload));
	};

export const setPropertyImagesUploadCompletedThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setPropertyImagesUploadCompleted(payload));
	};

export const setPropertyIsImagesUploadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setPropertyIsImagesUploading(payload));
	};

export const setServiceIsImagesUploadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setServiceIsImagesUploading(payload));
	};

export const setServiceImagesUploadCompletedThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setServiceImagesUploadCompleted(payload));
	};

export const setCreateNewPropertyDefaultLocationThunk =
	(payload: {
		location_coordinates: Array<number>;
		address: string;
		country: string;
		state_or_province: string;
		city: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setCreateNewPropertyDefaultLocation(payload));
	};

export const fetchAboutSinglePropertyThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAboutSingleProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-about-property", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSinglePropertyCreatorThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePropertyCreator(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-single-property-creators-details",
						{
							method: "POST",
							headers: {
								"Content-Type": "text/plain",
							},
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const fetchSinglePropertyAboutLocationThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePropertyAboutLocation(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-property-about-location",
						{
							method: "POST",
							headers: {
								"Content-Type": "text/plain",
							},
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const fetchSinglePropertyAmenitiesThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePropertyAmenities(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-property-amenities", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setSinglePropertyAmenitiesThunk =
	(
		payload: Array<{
			id: string;
			label: string;
			icon: string;
		}>,
	): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyAmenities(payload));
	};

export const setSinglePropertyAboutThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyAbout(payload));
	};

export const setSinglePropertyDeveloperNameThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyDeveloperName(payload));
	};
export const setSinglePropertyDeveloperSlugThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyDeveloperSlug(payload));
	};
export const setSinglePropertyEstablishedYearThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyEstablishedYear(payload));
	};

export const setSinglePropertyDescriptionThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyDescription(payload));
	};

export const setSinglePropertyAboutLocationThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyAboutLocation(payload));
	};

export const setSinglePropertyVicinityThunk =
	(
		payload: Array<{
			category: string;
			values: Array<{
				title: string;
				distance: number;
			}>;
		}>,
	): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyVicinity(payload));
	};

export const fetchSinglePropertyDeveloperThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePropertyDeveloper(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-property-developer", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSinglePropertyImagesThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePropertyImages(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-property-images", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSinglePropertyLocationCoordinatesThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePropertyLocationCoordinates(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-property-location-coordinates",
						{
							method: "POST",
							headers: {
								"Content-Type": "text/plain",
							},
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const fetchSinglePropertyOverviewThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePropertyOverview(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-property-overview", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setSinglePropertyOverviewThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyOverview(payload));
	};

export const setSinglePropertyManualOverviewThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSinglePropertyManualOverview(payload));
	};

export const fetchSinglePropertyReviewsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePropertyReviews(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-property-reviews", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSinglePropertyVicinityThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePropertyVicinity(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-property-vicinity", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSinglePropertyTrendingPropertiesThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePropertyTrendingProperties(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-trending-properties", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSinglePropertyHotPropertiesThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePropertyHotProperties(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-hot-properties", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSinglePropertyNeighbourhoodReviewsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSinglePropertyNeighbourhoodReviews(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/" + payload.slug + "/fetch-neighbourhood-reviews", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchAccountSettingsUserDataThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAccountSettingsUserData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/fetch-user-data", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchAccountSettingsUserCashtransactionsThunk =
	(payload: { slug: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAccountSettingsUserCashtransactions(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/fetch-user-cash-transactions", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchAccountSettingsUserBeegruPointstransactionsThunk =
	(payload: { slug: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAccountSettingsUserBeegruPointstransactions(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/fetch-user-beegru-points-transactions", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchAccountSettingsSubscriptionsPlansThunk =
	(payload: { slug: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAccountSettingsSubscriptionsPlans(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/fetch-subscriptions-plans", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchAccountSettingsSubscriptionsThunk =
	(payload: { slug: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAccountSettingsSubscriptions(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/fetch-subscriptions", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchAccountSettingsFilesCountThunk =
	(payload: { slug: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAccountSettingsFilesCount(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/fetch-file-counts", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setBusinessProfileAboutShortDescriptionThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfileAboutShortDescription(payload));
	};

export const setBusinessProfilePhoneCountryCodeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfilePhoneCountryCode(payload));
	};
export const setBusinessProfilePhoneNumberThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfilePhoneNumber(payload));
	};
export const setBusinessProfileAboutYearEstablishedThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfileAboutYearEstablished(payload));
	};
export const setBusinessProfileAboutOfficeAddressThunk =
	(
		payload: Array<{
			line1: string;
			line2: string;
			city: string;
			state: string;
			country: string;
			pincode: string;
			is_hq: boolean;
		}>,
	): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfileAboutOfficeAddress(payload));
	};
export const setBusinessProfileAboutWebsiteUrlThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfileAboutWebsiteUrl(payload));
	};

export const setBusinessProfileServicesOfferedTagsThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfileServicesOfferedTags(payload));
	};
export const setBusinessProfileExpertiseThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfileExpertise(payload));
	};

export const setBusinessProfileBrokerageThunk =
	(
		payload: Array<{
			brokerage_for: string;
			brokerage_percentage: number;
		}>,
	): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfileBrokerage(payload));
	};

export const setBusinessProfileLicencesAndCertificatesThunk =
	(
		payload: Array<{
			title: string;
			description: string;
			issued_by: string;
			issued_at: Date | string;
			media_url: string;
			mime_type: string;
		}>,
	): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfileLicencesAndCertificates(payload));
	};
export const setBusinessProfileNoOfEmployeesThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfileNoOfEmployees(payload));
	};
export const setBusinessProfileFAQThunk =
	(
		payload: Array<{
			question: string;
			answer: string;
		}>,
	): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfileFAQ(payload));
	};
export const setBusinessProfileLanguagesThunk =
	(
		payload: Array<{
			language: string;
			proficiency: string;
		}>,
	): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfileLanguages(payload));
	};

export const setBusinessProfileEmailThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfileEmail(payload));
	};

export const deleteReviewThunk =
	(payload: { entity_id: string | ObjectId; source: string; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.deleteReview({
				source: payload.source,
				response: await (
					await fetch(process.env.PRODUCTION_API_URL + "reviews/delete-review", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			}),
		);
	};

export const CopyListPropertyThunk =
	(payload: {
		property_id: string | ObjectId;
		slug: string;
		creator_is_business_profile: boolean;
		creator_user_id: string | ObjectId;
		creator_business_profile_id: string | ObjectId;
		profile_context_is_business_profile: boolean;
		profile_context_user_id: string | ObjectId;
		profile_context_business_profile_id: string | ObjectId;
		linked: boolean;
		linked_to: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.createNewProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/copy-listing-property", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const AllowcopyPropertyThunk =
	(payload: { property_id: string | ObjectId; allow_copy: boolean }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.AllowcopyProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/allow-copy-property", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const ShowContactDetailsPropertyThunk =
	(payload: {
		property_id: string | ObjectId;
		show_contact_details: boolean;
		sessionId: ObjectId | string;
		profile_context: StateInterface["profile_context"];
		business_profile_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.ShowContactDetailsProperty(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/edit-property/show-contact-details-property", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const ShowContactDetailsServiceThunk =
	(payload: { service_id: string | ObjectId; show_contact_details: boolean; sessionId: ObjectId | string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.ShowContactDetailsService(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/edit-service/show-contact-details-service", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const AddingBeegruPointsForSharePropertyThunk =
	(payload: { user_id: string | ObjectId; url: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.addingbeegrupointsshareproperty(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "properties/edit-property/adding-beegru-points-for-share-property",
						{
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const AddingBeegruPointsForShareServiceThunk =
	(payload: { user_id: string | ObjectId; url: string; sessionId: ObjectId | string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.AddingBeegruPointsForShareService(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "services/edit-services/adding-beegru-points-for-share-service",
						{
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const fetchBusinessProfilePostsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfilePosts(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-business-profiles-posts", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const LoadingMoreBusinessProfilePostsThunk =
	(payload: { business_id: string | ObjectId; sessionId: string; pageNumber: string; pageSize: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.LoadingMoreBusinessProfilePosts(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/load-more-posts", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setPropertySnackbarThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setPropertySnackbar(payload));
	};

export const setServiceSnackbarThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setServiceSnackbar(payload));
	};

export const setAccountSettingsSnackbarThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setAccountSettingSnackbar(payload));
	};

export const searchUserPropertiesThunk =
	(payload: { search_term: string; sessionId: string; user_id: string | ObjectId; skip: number }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.searchUserProperties(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/property-search-results", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const setHomeLoadingThunk =
	(payload: StateInterface["home_loading"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setHomeLoading(payload));
	};
export const setUserLoadingThunk =
	(payload: StateInterface["user_profile_loading"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setUserLoading(payload));
	};
export const setHomePagePropertiesLoadingThunk =
	(loading: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setHomePagePropertiesLoading(loading));
	};
export const searchBusinessProfilePropertiesThunk =
	(payload: {
		search_term: string;
		sessionId: string;
		business_profile_id: string | ObjectId;
		business_profile_slug: string;
		skip: number;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.searchBusinessProfileProperties(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/property-search-results", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const searchBusinessProfileOfbPropertiesThunk =
	(payload: {
		search_term: string;
		sessionId: string;
		business_profile_id: string | ObjectId;
		business_profile_slug: string;
		skip: number;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.searchBusinessProfileOfbProperties(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/ofb-property-search-results", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const searchOfbPropertyListingsThunk =
	(payload: {
		search_term: string;
		sessionId: string;
		business_profile_id: string | ObjectId;
		business_profile_slug: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.searchOfbPropertyListings(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/search-ofb-property-listings", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const searchBusinessProfileServicesThunk =
	(payload: {
		search_term: string;
		sessionId: string;
		business_profile_id: string | ObjectId;
		business_profile_slug: string;
		skip: number;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.searchBusinessProfileServices(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/services-search-results", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const searchServiceListingsThunk =
	(payload: {
		search_term: string;
		sessionId: string;
		business_profile_id: string | ObjectId;
		business_profile_slug: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.searchServiceListings(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/search-service-listings", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setBusinessProfileSnackbarThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessProfileSnackbar(payload));
	};
export const LoadingMoreUserGivenReviewsThunk =
	(payload: {
		user_id: string | ObjectId;
		pageNumber: string;
		pageSize: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.LoadingMoreUserGivenReviews(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/load-more-given-reviews", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const LoadingMoreUserReviewsThunk =
	(payload: {
		user_id: string | ObjectId;
		sessionId: string | ObjectId;
		pageNumber: string;
		pageSize: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.LoadingMoreUserReviews(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/load-more-reviews", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessProfileGivenReviewsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileGivenReviews(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-business-profile-given-reviews", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const LoadingMoreBusinessProfileGivenReviewsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		sessionId: string;
		pageNumber: string;
		pageSize: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.LoadingMoreBusinessProfileGivenReviews(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/load-more-business-profile-given-reviews", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const LoadingMoreBusinessProfileReviewsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		sessionId: string;
		pageNumber: string;
		pageSize: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.LoadingMoreBusinessProfileReviews(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/load-more-reviews", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setTransactionTypeThunk =
	(payload: StateInterface["transaction"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setTransactionType(payload));
	};

export const setOnBoardingDetailsThunk =
	(payload: StateInterface["on_boarding_details"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setOnBoardingDetails(payload));
	};

export const fetchOnBoardingBusinessTitleThunk = (): AppThunk => async (dispatch) => {
	dispatch(
		globalSlice.actions.fetchOnBoardingBusinessTitle(
			await (
				await fetch(process.env.PRODUCTION_API_URL + "on-boarding/fetch-business-title", {
					method: "POST",
					cache: "no-store",
					body: "",
				})
			).json(),
		),
	);
};

export const setUserProfileSnackbarThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setUserProfileSnackbar(payload));
	};

export const NewEmployeeRquestOnBoardingThunk =
	(payload: {
		employee_is_business_profile: boolean;
		business_profile_id: ObjectId | string;
		editor_user_id: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.NewEmployeeRquestOnBoarding(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "on-boarding/new-employee-request", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchUserProfileLeadsThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchUserProfileLeads(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/fetch-leads", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setBottomBuyBeegruPointsThunk =
	(payload: StateInterface["bottom_subscribe_buy_beegru_points"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setBottomBuyBeegruPoints(payload));
	};

export const setDefaultProfileContextThunk =
	(payload: StateInterface["profile_context"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setDefaultProfileContext(payload));
	};

export const updateReduxSessionThunk =
	(payload: { session: StateInterface["redux_session"] }): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.updateReduxSession(payload));
	};

export const UpdateReduxSessionUserFieldsThunk =
	(payload: { first_name?: string; last_name?: string; profile_picture?: any }): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.UpdateReduxSessionUserFields(payload));
	};

export const FetchMoreBusinessProfilesThunk =
	(payload: { user_id: ObjectId | string; pages: Array<{ page_id: ObjectId | string }> }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.FetchMoreBusinessProfiles(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "fetch-more-business-profiles", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessProfilePublishedPropertiesThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfilePublishedProperties(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-published-properties", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updatedPublishedPropertiesThunk =
	(payload: {
		selectedProperties: Array<string>;
		filterProperties: Array<any>;
		action: string;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updatedPublishedProperties(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/update-published-properties", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessProfilePublishedServicesThunk =
	(payload: { slug: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfilePublishedServices(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/fetch-published-services", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updatedPublishedServicesThunk =
	(payload: {
		selectedProperties: Array<string>;
		filterProperties: Array<any>;
		action: string;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updatedPublishedServices(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/update-published-services", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchAccountSettingsRewardTransactionThunk =
	(payload: { slug: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAccountSettingsRewardtransactions(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/fetch-rewards-history", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchOneTimeRewardsThunk =
	(payload: { slug: string; skip: number }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchOneTimeRewards(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/fetch-rewards-one-time", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchRecurringRewardsThunk =
	(payload: { slug: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchRecurringRewards(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/fetch-recurring-rewards", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateSearchFilterActiveOptionsThunk =
	({
		searchFilterId,
		newOptions,
	}: {
		searchFilterId: SearchFilterId;
		newOptions: Array<SearchFilterOption>;
	}): AppThunk =>
	(dispatch: Function) => {
		dispatch(globalSlice.actions.updateSearchFilterActiveOptions({ searchFilterId, newOptions }));
	};

export const updateSearchFilterSelectedOptionsThunk =
	({
		searchFilterId,
		newOptions,
	}: {
		searchFilterId: SearchFilterId;
		newOptions: Array<SearchFilterOption>;
	}): AppThunk =>
	(dispatch: Function) => {
		dispatch(globalSlice.actions.updateSearchFilterSelectedOptions({ searchFilterId, newOptions }));
	};

export const updateSearchFilterVisibilityThunk =
	({ searchFilterId, isVisible }: { searchFilterId: SearchFilterId; isVisible: boolean }): AppThunk =>
	(dispatch: Function) => {
		dispatch(globalSlice.actions.updateSearchFilterVisibility({ searchFilterId, isVisible }));
	};

export const updateSearchFilterAppliedThunk =
	({ searchFilterId, isApplied }: { searchFilterId: SearchFilterId; isApplied: boolean }): AppThunk =>
	(dispatch: Function) => {
		dispatch(globalSlice.actions.updateSearchFilterApplied({ searchFilterId, isApplied }));
	};

export const updateSearchFilterIsAccordionOpenThunk =
	({ searchFilterId, isAccordionOpen }: { searchFilterId: SearchFilterId; isAccordionOpen: boolean }): AppThunk =>
	(dispatch: Function) => {
		dispatch(globalSlice.actions.updateSearchFilterIsAccordionOpen({ searchFilterId, isAccordionOpen }));
	};

export const setSearchFilterMetricesThunk =
	({
		boosted,
		non_boosted,
		filter_hash,
		no_of_boosted_results,
		no_of_non_boosted_results,
		total_no_of_results,
		boosted_from_other_locations,
		no_of_boosted_from_other_locations,
	}: {
		boosted: Array<number>;
		non_boosted: Array<number>;
		filter_hash: string;
		no_of_boosted_results: number;
		no_of_non_boosted_results: number;
		total_no_of_results: number;
		boosted_from_other_locations: Array<number>;
		no_of_boosted_from_other_locations: number;
	}): AppThunk =>
	(dispatch: Function) => {
		dispatch(
			globalSlice.actions.setSearchFilterMetrices({
				boosted,
				non_boosted,
				filter_hash,
				no_of_boosted_results,
				no_of_non_boosted_results,
				total_no_of_results,
				boosted_from_other_locations,
				no_of_boosted_from_other_locations,
			}),
		);
	};

export const CreateNewViewThunk =
	(payload: {
		entity_creator_user_id: string | ObjectId;
		viewed_entity_type: string;
		viewed_entity_id: string | ObjectId;
		viewer_is_logged_in: boolean;
		viewer_is_business_profile: boolean;
		viewer_user_id: string | ObjectId;
		viewer_business_profile_id: string | ObjectId;
		viewer_session_id: string | ObjectId;
		viewer_ip_address: string | null;
		viewer_user_agent: string | null;
		source: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CreateNewView(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "analytics/create-new-view", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setSearchAnimationDetailsThunk =
	(payload: StateInterface["search_animation_settings"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setSearchAnimationDetails(payload));
	};

export const fetchSearchThunk =
	({
		db_queries,
		page_number,
		page_size,
		search_filter_metrices,
		session_id,
		user_id,
		profile_context,
		sort_field,
		search_term,
	}: {
		db_queries: output_type_of_database_query;
		page_number: number;
		page_size: string | number | undefined;
		search_filter_metrices: SearchFilterMetrices;
		session_id: string | ObjectId;
		user_id: string | ObjectId;
		profile_context: profile_contextInterface | null;
		sort_field?: string;
		search_term?: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSearchData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "search-api", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({
							db_queries,
							page_number,
							page_size,
							search_filter_metrices,
							session_id,
							user_id,
							profile_context,
							sort_field,
							search_term,
						}),
					})
				).json(),
			),
		);
	};
export const fetchPropertyCardThunk =
	({ _id }: { _id: any }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchPropertyCardData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "properties/cards/" + _id, {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({
							_id,
						}),
					})
				).json(),
			),
		);
	};

// PropertyBoost
type PropertyBoostMobileStep = {
	step: number;
};
export const SetPropertyBoostMobileStepStateThunk =
	(payload: PropertyBoostMobileStep): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.SetPropertyBoostMobileStepState(payload));
	};

type PropertyBoostSnack = {
	snack_state: boolean;
	snack_message: string;
	snack_severity: OverridableStringUnion<AlertColor, AlertPropsColorOverrides>;
};
export const SetPropertyBoostSnackStateThunk =
	(payload: PropertyBoostSnack): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.SetPropertyBoostSnackState(payload));
	};

type PropertyBoostModal = {
	state?: boolean;
	id?: string | ObjectId;
	title?: string;
	createdBy?: any;
	active?: boolean;
};

export const SetPropertyBoostModalStateThunk =
	(payload: PropertyBoostModal): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.SetPropertyBoostModalState(payload));
	};

export const SetPropertyBoostedByStateThunk =
	(payload: BoostInterface["boosted_by"]): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.SetPropertyBoostedByState(payload));
	};

type PropertyBoostData = { slug: string | ObjectId };
export const SetPropertyBoostDataStateThunk =
	(payload: PropertyBoostData): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.SetPropertyBoostDataState(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "boost-property/fetch-property-detail", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

type PropertyBoostDetail = {
	snack?: boolean;
	weeks_count?: number;
	start_date?: string;
	temp_start_date?: string;
};
export const SetPropertyBoostDetailStateThunk =
	({ snack = true, ...payload }: PropertyBoostDetail): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.SetPropertyBoostDetailState({ ...payload, snack }));
	};

type PropertyBoostLocation = {
	snack?: boolean;
	location?: BoostLocation[];
	tempLocation?: BoostLocation[];
	selected?: BoostLocation | null;
	newBoostId?: string;
};
export const SetPropertyBoostLocationStateThunk =
	({ snack = true, ...payload }: PropertyBoostLocation): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.SetPropertyBoostLocationState({ ...payload, snack }));
	};

type PropertyBoostPoints = {
	snack?: boolean;
	points?: number;
	budget?: number;
	availablePoints?: number;
	checkAvailablePoints?: number;
};
export const SetPropertyBoostPointsStateThunk =
	({ snack = true, ...payload }: PropertyBoostPoints): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.SetPropertyBoostPointsState({ ...payload, snack }));
	};

type PropertyLoading = {
	submitLoading?: boolean;
};
export const SetPropertyLoadingStateThunk =
	(payload: PropertyLoading): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.SetPropertyLoadingState(payload));
	};

type CreatePropertyBoost = {
	boost: BoostInterface;
	sessionId?: string | ObjectId;
};
export const CreatePropertyBoostStateThunk =
	(payload: CreatePropertyBoost): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CreatePropertyBoostState(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "boost-property/create-property-boost", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

type CancelPropertyBoost = {
	boost: PropertyBoostInterface;
	sessionId?: string | ObjectId;
	locale: string;
};

export const CancelPropertyBoostStateThunk =
	(payload: CancelPropertyBoost): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CancelPropertyBoostState(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "boost-property/cancel-property-boost", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdatePropertyTitleThunk =
	(payload: {
		sessionId: string | ObjectId;
		phoneNumber: string;
		countryCode: string;
		property_id: string | ObjectId;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		property_title: string;
		slug: string;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdatePropertyTitle(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/properties/edit-property/update-property-title", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateBusinessProfileLeadsStatusThunk =
	(payload: { status: any; leadsId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessProfileLeadsStatus(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/leads-status", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const UpdateBusinessProfileLeadsNotesThunk =
	(payload: { leadsId: string | ObjectId; notes: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessProfileLeadsNotes(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-profiles/leads-notes", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateUserProfileLeadsStatusThunk =
	(payload: { status: any; leadsId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateUserProfileLeadsStatus(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/leads-status", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const UpdateUserProfileLeadsNotesThunk =
	(payload: { leadsId: string | ObjectId; notes: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateUserProfileLeadsNotes(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/leads-notes", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const UpdateServiceTitleThunk =
	(payload: {
		phoneNumber: string;
		countryCode: string;
		service_id: string | ObjectId;
		editor_is_business_profile: boolean;
		editor_user_id: string | ObjectId;
		editor_business_profile_id: string | ObjectId;
		service_title: string;
		slug: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateServiceTitle(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "services/edit-services/update-service-title", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setLoadingThunk =
	(payload: StateInterface["loading"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setLoading(payload));
	};

export const updateBeegrupointsAfterPurchaseThunk =
	(payload: number): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.updateBeegrupointsAfterPurchase(payload));
	};

export const fetchLeadsByIdThunk =
	({ leadsId }: { leadsId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchLeadsById(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/leads" + leadsId, {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({
							leadsId,
						}),
					})
				).json(),
			),
		);
	};

export const setBusinessDashboardCurrentScreenThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardCurrentScreen(payload));
	};

export const setBusinessDashboardShowContactDetailsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardShowContactDetailsLoading(payload));
	};

export const setBusinessDashboardPromotionalEmailsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardPromotionalEmailsLoading(payload));
	};

export const fetchBusinessDashboardSavedItemsThunk =
	(payload: { business_profile_id: string | ObjectId; listing_type: string; page: number }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardSavedItems(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/saved/fetch-saved-items", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setBusinessDashboardSavedItemsTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardSavedItemsTriggerNewSearch(payload));
	};

export const setBusinessDashboardSavedItemsInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardSavedItemsInitialLoadDone(payload));
	};

export const setBusinessDashboardSavedItemsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardSavedItemsLoading(payload));
	};

export const setBusinessDashboardSavedItemsFilterListingTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardSavedItemsFilterListingType(payload));
	};

export const setBusinessDashboardSavedItemsFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardSavedItemsFilterPageNumber(payload));
	};

/*

& Every leads fetch goes through one of the two thunks below, and some dozen places on the leads screen build their own
& payload: each used to drop the date range, and the "All AI Boost campaigns" choice was sent as its placeholder id, which
& the route strips (so the filter vanished). The thunks complete the payload from the saved filters instead:
&   - date_range_filter: the one passed, else the saved filter_date_range (empty dates send none).
&   - filter_assigned_to_user_ids: the one passed, else the saved teammates (the route ignores it for a member).
&   - boost_ids: "all-campaigns" becomes every campaign the business has (the route's own campaign list, or the boosts
&     the AI Boost details screen handed over before that list loaded); any other placeholder is dropped as before.

*/

const ALL_CAMPAIGNS: string = "all-campaigns";
/* The Source menu's "Requirement" choice (gap A4): leads from claimed requirements, sent as from_requirement. */
const FROM_REQUIREMENT: string = "from-requirement";

const completeLeadsRequest = <
	T extends {
		boost_ids: Array<string | ObjectId>;
		date_range_filter?: { start_date: string; end_date: string };
		filter_assigned_to_user_ids?: Array<string>;
	},
>(
	payload: T,
	state: StateInterface,
): T => {
	/* The slice starts without business_dashboard (it is hydrated by the page), so a missing one sends the payload as is. */
	const filters = state?.business_dashboard?.search_leads_response;
	if (!filters) return payload;
	const requested: Array<string> = (payload.boost_ids || []).map((id) => String(id));
	const allCampaignIds: Array<string> = (
		filters.filter_campaign_ids?.length
			? filters.filter_campaign_ids.map((campaign) => campaign.boost_id)
			: filters.filter_boost_ids || []
	).map((id) => String(id));
	const savedRange = filters.filter_date_range;
	const fromRequirement: boolean = requested.includes(FROM_REQUIREMENT);
	return {
		...payload,
		boost_ids: fromRequirement ? [] : requested.includes(ALL_CAMPAIGNS) ? allCampaignIds : requested,
		from_requirement: fromRequirement,
		// A claimed requirement's lead has no listing type, so the default "property" would hide every one of them.
		...(fromRequirement ? { listing_type: "all" } : {}),
		filter_assigned_to_user_ids: payload.filter_assigned_to_user_ids ?? filters.filter_assigned_to_user_ids ?? [],
		date_range_filter:
			payload.date_range_filter ??
			(savedRange && savedRange.start_date && savedRange.end_date
				? { start_date: savedRange.start_date, end_date: savedRange.end_date }
				: undefined),
	};
};

export const fetchBusinessDashboardLeadsDesktopThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		boost_ids: Array<string | ObjectId>;
		status: string;
		assigned_status: string;
		listing_type: string;
		property_id: string | ObjectId;
		sort_field: string;
		sort_order: number;
		page: number;
		search_text?: string;
		lead_id?: string | ObjectId;
		date_range_filter?: { start_date: string; end_date: string };
		filter_assigned_to_user_ids?: Array<string>;
		logged_in_user_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch, getState) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardLeadsDesktop(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/leads/fetch-leads-desktop", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(completeLeadsRequest(payload, getState().globalState as unknown as StateInterface)),
					})
				).json(),
			),
		);
	};

export const fetchBusinessDashboardLeadsMobileThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		boost_ids: Array<string | ObjectId>;
		status: string;
		assigned_status: string;
		listing_type: string;
		property_id: string | ObjectId;
		sort_field: string;
		sort_order: number;
		page: number;
		search_text?: string;
		lead_id?: string | ObjectId;
		date_range_filter?: { start_date: string; end_date: string };
		filter_assigned_to_user_ids?: Array<string>;
		logged_in_user_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch, getState) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardLeadsMobile(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/leads/fetch-leads-mobile", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(completeLeadsRequest(payload, getState().globalState as unknown as StateInterface)),
					})
				).json(),
			),
		);
	};

export const setFilterSelectedCampaignIdThunk =
	(payload: string | ObjectId): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setFilterSelectedCampaignId(payload));
	};

export const setSearchLeadsResponseBoostIdsThunk =
	(payload: Array<string | ObjectId>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSearchLeadsResponseBoostIds(payload));
	};

export const setBusinessDashboardLeadsTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardLeadsTriggerNewSearch(payload));
	};

export const setBusinessDashboardLeadsInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardLeadsInitialLoadDone(payload));
	};

export const setBusinessDashboardLeadsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardLeadsLoading(payload));
	};

export const setBusinessDashboardLeadsFilterStatusThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardLeadsFilterStatus(payload));
	};

export const setBusinessDashboardLeadsFilterAssignedStatusThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardLeadsFilterAssignedStatus(payload));
	};

export const setBusinessDashboardLeadsFilterAssignedToUserIdsThunk =
	(payload: Array<string>): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardLeadsFilterAssignedToUserIds(payload));
	};

export const setBusinessDashboardLeadsFilterDateRangeThunk =
	(payload: { option: string; start_date: string; end_date: string }): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardLeadsFilterDateRange(payload));
	};

export const setBusinessDashboardLeadsFilterPropertyThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardLeadsFilterProperty(payload));
	};

export const setBusinessDashboardLeadsFilterListingTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardLeadsFilterListingType(payload));
	};

export const setBusinessDashboardLeadsFilterSortFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardLeadsFilterSortField(payload));
	};

export const setBusinessDashboardLeadsFilterSortOrderThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardLeadsFilterSortOrder(payload));
	};

export const setBusinessDashboardLeadsFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardLeadsFilterPageNumber(payload));
	};

export const setBusinessDashboardReviewsTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardReviewsTriggerNewSearch(payload));
	};

export const setBusinessDashboardReviewsInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardReviewsInitialLoadDone(payload));
	};
export const setBusinessDashboardReviewsFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardReviewsFilterPageNumber(payload));
	};

type LoggedUser = {
	is_business_profile: boolean;
	user_id: string | ObjectId;
	business_profile_id: string | ObjectId;
};

export const fetchBusinessDashboardReviewsMobileThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		sort_field: string;
		sort_order: number;
		page: number;
		loggedUser: LoggedUser;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardReviewsMobile(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/reviews/fetch-reviews-mobile", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const setBusinessDashboardReviewsFilterSortFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardReviewsFilterSortField(payload));
	};

export const setBusinessDashboardReviewsFilterSortOrderThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardReviewsFilterSortOrder(payload));
	};

export const setBusinessDashboardBeegruPointTransactionsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBeegruPointTransactionsLoading(payload));
	};

export const setBusinessDashboardBeegruPointTransactionsTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBeegruPointTransactionsTriggerNewSearch(payload));
	};

export const setBusinessDashboardBeegruPointTransactionsInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBeegruPointTransactionsInitialLoadDone(payload));
	};

export const setBusinessDashboardBeegruPointTransactionsFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBeegruPointTransactionsFilterPageNumber(payload));
	};

export const fetchBusinessDashboardBeegruPointTransactionsThunk =
	(payload: { sessionId: string | ObjectId; user_id: string | ObjectId; page: number }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardBeegruPointTransactions(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/settings/fetch-beegru-point-transactions", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setBusinessDashboardCashTransactionsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardCashTransactionsLoading(payload));
	};

export const setBusinessDashboardCashTransactionsTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardCashTransactionsTriggerNewSearch(payload));
	};

export const setBusinessDashboardCashTransactionsInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardCashTransactionsInitialLoadDone(payload));
	};

export const setBusinessDashboardCashTransactionsFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardCashTransactionsFilterPageNumber(payload));
	};

export const fetchBusinessDashboardCashTransactionsThunk =
	(payload: { sessionId: string | ObjectId; user_id: string | ObjectId; page: number }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardCashTransactions(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/settings/fetch-cash-transactions", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setBusinessDashboardPaymentTransactionsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardPaymentTransactionsLoading(payload));
	};

export const setBusinessDashboardPaymentTransactionsTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardPaymentTransactionsTriggerNewSearch(payload));
	};

export const setBusinessDashboardPaymentTransactionsInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardPaymentTransactionsInitialLoadDone(payload));
	};

export const setBusinessDashboardPaymentTransactionsFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardPaymentTransactionsFilterPageNumber(payload));
	};

export const fetchBusinessDashboardPaymentTransactionsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		user_id: string | ObjectId;
		page: number;
		search_text?: string;
		status?: string;
		time_period?: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardPaymentTransactions(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/settings/fetch-payment-transactions", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

//rewards history

export const setBusinessDashboardRewardsHistoryLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardRewardsHistoryLoading(payload));
	};

export const setBusinessDashboardRewardsHistoryTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardRewardsHistoryTriggerNewSearch(payload));
	};

export const setBusinessDashboardRewardsHistoryInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardRewardsHistoryInitialLoadDone(payload));
	};

export const setBusinessDashboardRewardsHistoryFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardRewardsHistoryFilterPageNumber(payload));
	};

export const fetchBusinessDashboardRewardsHistoryThunk =
	(payload: { sessionId: string | ObjectId; user_id: string | ObjectId; page: number }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardRewardsHistory(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/settings/fetch-rewards-history", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

//rewards

export const setBusinessDashboardRewardsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardRewardsLoading(payload));
	};

export const setBusinessDashboardRewardsTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardRewardsTriggerNewSearch(payload));
	};

export const setBusinessDashboardRewardsInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardRewardsInitialLoadDone(payload));
	};

export const fetchBusinessDashboardRewardsThunk =
	(payload: { sessionId: string | ObjectId; user_id: string | ObjectId; page: number }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardRewards(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/settings/fetch-rewards", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

//referral

export const setBusinessDashboardReferralsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardReferralsLoading(payload));
	};

export const setBusinessDashboardReferralsTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardReferralsTriggerNewSearch(payload));
	};

export const setBusinessDashboardReferralsInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardReferralsInitialLoadDone(payload));
	};

export const setBusinessDashboardReferralsFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardReferralsFilterPageNumber(payload));
	};

export const fetchBusinessDashboardReferralsThunk =
	(payload: { sessionId: string | ObjectId; user_id: string | ObjectId; page: number }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardReferrals(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/settings/fetch-referrals", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

// business dashboard kyc

export const fetchBusinessDashboardKycInfoThunk =
	(payload: { profile_to_verify_id: ObjectId | string; profile_to_verify_type: string; sessionId: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardKycInfo(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/settings/fetch-kyc-info", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setBusinessDashboardListingsTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardListingsTriggerNewSearch(payload));
	};

export const setBusinessDashboardBroadcastTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBroadcastTriggerNewSearch(payload));
	};

export const setBusinessDashboardListingsInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardListingsInitialLoadDone(payload));
	};

export const setBusinessDashboardListingsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardListingsLoading(payload));
	};

export const setBusinessDashboardListingsFilterStatusThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardListingsFilterStatus(payload));
	};

export const setBusinessDashboardListingsFilterListingTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardListingsFilterListingType(payload));
	};

export const setBusinessDashboardListingsFilterPropertyIdThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardListingsFilterPropertyId(payload));
	};

export const setBusinessDashboardListingsFilterTransactionTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardListingsFilterTransactionType(payload));
	};

export const setBusinessDashboardListingsFilterSortFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardListingsFilterSortField(payload));
	};

export const setBusinessDashboardBroadcastFilterSortFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBroadcastFilterSortField(payload));
	};

export const setBusinessDashboardBroadcastFilterStatusThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBroadcastFilterStatus(payload));
	};

export const setBusinessDashboardListingsFilterSortOrderThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardListingsFilterSortOrder(payload));
	};

export const setBusinessDashboardBroadcastFilterSortOrderThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBroadcastFilterSortOrder(payload));
	};

export const setBusinessDashboardListingsFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardListingsFilterPageNumber(payload));
	};

export const fetchBusinessDashboardListingsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		status: string;
		listing_type: string;
		property_id: string | ObjectId;
		sort_field: string;
		sort_order: number;
		page: number;
		transaction_type?: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardListings(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/listings/fetch-listings", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessDashboardNewListingsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		status: string;
		listing_type: string;
		property_id: string | ObjectId;
		sort_field: string;
		sort_order: number;
		page: number;
		transaction_type?: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardListings(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/listings/fetch-listings-new", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessProfileUserViewListingsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		listing_type: string;
		transaction_type: string;
		sort_field: string;
		sort_order: number;
		page: number;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessProfileUserViewListings(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/user-view/listings/fetch-listings", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessDashboardAdminsThunk =
	(payload: { business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardAdmins(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/admin/fetch-admins-details", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setBusinessDashboardAdminLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardAdminLoading(payload));
	};

/*
 * Legacy Admins tab "add": since 6 Oct 2026 the route sends a team INVITE rather than adding
 * the person (see pages/api/business-dashboard/admin/add-admin.ts). The response is returned
 * so the screen can tell "invite sent" from "already on the team" or a refusal; its payload
 * is null and the screen re-fetches the list.
 */
export const businessDashboardAddAdminUserDetailsThunk =
	(payload: {
		business_profile_id: string;
		editor_user_id: string;
		access_level: string;
		url: string;
		sessionId: string | ObjectId;
		profile_context: profile_contextInterface | null;
	}): AppThunk<Promise<any>> =>
	async (dispatch) => {
		const response = await (
			await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/admin/add-admin", {
				method: "POST",
				cache: "no-store",
				body: JSON.stringify(payload),
			})
		).json();
		dispatch(globalSlice.actions.businessDashboardAddAdminUserDetails(response));
		return response;
	};

export const businessDashboardDeleteAdminUserDetailsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		editor_user_id: string | ObjectId;
		sessionId: string | ObjectId;
		url?: string;
		profile_context?: profile_contextInterface | null;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.businessDashboardDeleteAdminUserDetails(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/admin/remove-admin", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessDashboardLeadsOverviewThunk =
	(payload: { business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardLeadsOverview(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/leads/leads-overview", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchBusinessDashboardPropertiesOverviewThunk =
	(payload: { business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardPropertiesOverview(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/listings/properties-overview", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const switchBusinessDashboardContantDetailsThunk =
	(payload: {
		sessionId: string | ObjectId;
		show_contact_details: boolean;
		business_profile_id: string | ObjectId;
		profile_context: profile_contextInterface | null;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.switchBusinessDashboardContantDetails(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/show-hide-contact-details", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const switchBusinessDashboardPromotionalEmailsThunk =
	(payload: {
		sessionId: string | ObjectId;
		user_id: string | ObjectId;
		profile_context: profile_contextInterface | null;
		receive_promotional_emails: boolean;
		url: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.switchBusinessDashboardPromotionalEmails(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/settings/update-receive-promotional-emails",
						{
							method: "POST",
							cache: "no-store",
							headers: {
								"Content-Type": "text/plain",
							},
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const fetchBusinessDashboardOFBsOverviewThunk =
	(payload: { business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardOFBsOverview(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/listings/ofbs-overview", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessDashboardServicesOverviewThunk =
	(payload: { business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardServicesOverview(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/listings/services-overview", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchBusinessDashboardBoostsMobileThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		status: string;
		listing_type: string;
		sort_field: string;
		sort_order: number;
		page: number;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardBoostsMobile(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/boosts/fetch-boosts", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setPropertyIdForCampaignDetailsThunk =
	(payload: { property_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setPropertyIdForCampaignDetails(payload));
	};

// export const setBusinessDashboardAiBoostFormThunk =
// 	(payload: {
// 		boost_form_data: {
// 			no_of_leads: number;
// 			budget: number;
// 			campaign_duration: {
// 				start_date: string;
// 				in_months: number;
// 			};
// 			notes: string;
// 			creative_needed: boolean;
// 			creative_headline: string;
// 		};
// 	}): AppThunk =>
// 	(dispatch) => {
// 		dispatch(globalSlice.actions.setBusinessDashboardAiBoostForm(payload));
// 	};

export const setBusinessDashboardBoostsTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBoostsTriggerNewSearch(payload));
	};

export const setBusinessDashboardBoostsInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBoostsInitialLoadDone(payload));
	};

export const setBusinessDashboardBoostsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBoostsLoading(payload));
	};

export const setBusinessDashboardBoostsFilterStatusThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBoostsFilterStatus(payload));
	};

export const setBusinessDashboardBoostsFilterListingTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBoostsFilterListingType(payload));
	};

export const setBusinessDashboardBoostsFilterSortFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBoostsFilterSortField(payload));
	};

export const setBusinessDashboardBoostsFilterSortOrderThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBoostsFilterSortOrder(payload));
	};

export const setBusinessDashboardBoostsFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardBoostsFilterPageNumber(payload));
	};

export const UpdateBusinessProfileTitleThunk =
	(payload: {
		sessionId: string | ObjectId;
		business_profile_id: string | ObjectId;
		editor_is_business_profile?: boolean;
		editor_user_id?: string | ObjectId;
		editor_business_profile_id?: string | ObjectId;
		business_profile_title: string;
		old_business_profile_title: string;
		slug: string;
		profile_context: profile_contextInterface | null;
		url?: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessProfileTitle(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/profile/update-business-profile-title", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

/* Remove the business photo (Account #50, the app's Remove photo): the owner or an admin only (the route checks).
   On success the dashboard shows no photo; answers the route's result so the screen can say why it was refused. */
export const RemoveBusinessProfilePictureThunk =
	(payload: { business_profile_id: ObjectId | string; file_id: ObjectId | string; is_user_owner: boolean }): AppThunk =>
	async (dispatch) => {
		const result = await fetch(process.env.PRODUCTION_API_URL + "business-profiles/delete-profile-picture", {
			method: "POST",
			cache: "no-store",
			body: JSON.stringify({ business_profile_id: payload.business_profile_id, file_id: payload.file_id }),
		})
			.then((res) => res.json())
			.catch(() => null);
		if (result?.status === "success") {
			dispatch(
				/* The upload route's payload shape, which this reducer reads (its prepare type says otherwise). */
				globalSlice.actions.UpdateBusinessProfilePicture({
					profile_picture: { file_id: "", mime_type: "", exists: false },
					is_user_owner: payload.is_user_owner,
				} as any),
			);
		}
		return result;
	};

export const UpdateBusinessProfilePictureThunk =
	(payload: {
		value: string;
		business_profile_id: ObjectId | string;
		profile_picture_file_id: ObjectId | string;
		mime_type: string | undefined;
		sessionId: ObjectId | string;
		url: string;
		editor_is_business_profile: boolean;
		editor_business_profile_id: ObjectId | string;
		editor_user_id: ObjectId | string;
		profile_context: profile_contextInterface | null;
		is_user_owner: boolean;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessProfilePicture(
				(
					await (
						await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/profile/update-profile-picture", {
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						})
					).json()
				).payload,
			),
		);
	};

export const UpdateBusinessProfileLanguagesThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		language: Array<{ language: string; proficiency: string }>;
		url?: string;
		profile_context?: profile_contextInterface | null;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessProfileLanguages(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/profile/update-languages", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateBusinessProfileAboutDetailsThunk =
	(payload: {
		sessionId: string | ObjectId;
		business_profile_id: string | ObjectId;
		description: string;
		phone_number: string;
		country_code: string;
		email: string;
		year_established: number;
		website_url: string;
		url?: string;
		editor_is_business_profile?: boolean;
		editor_user_id?: string | ObjectId;
		editor_business_profile_id?: string | ObjectId;
		profile_context?: profile_contextInterface | null;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessProfileAboutDetails(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/profile/update-business-profile-about-details",
						{
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};
export const UpdateBusinessProfileAreasOfOperationThunk =
	(payload: {
		business_profile_id: ObjectId | string;
		areas_of_operation:
			| Array<{
					title: string;
					pincode: string;
					location_coordinates: Array<number>;
			  }>
			| [];
		regulatory_permits?: Array<{ permit_name: string; permit_number: string }>;
		editor_is_business_profile?: boolean;
		editor_user_id?: string | ObjectId;
		editor_business_profile_id?: string | ObjectId;
		url?: string;
		profile_context?: profile_contextInterface | null;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessProfileAreasOfOperation(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/profile/update-areas-of-operation", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateBusinessProfileSpecializationThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		property_type_specialization: Array<string> | [];
		url: string;
		sessionId: string | ObjectId;
		profile_context: profile_contextInterface;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessProfileSpecialization(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/profile/update-property-type-specialization",
						{
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const UpdateBusinessProfileListingsCountThunk =
	(payload: { business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateBusinessProfileListingsCount(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/user-view/listings/fetch-listings-count", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setBusinessDashboardAnalyticsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardAnalyticsLoading(payload));
	};

export const setBusinessDashboardAnalyticsGraphSourceThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardAnalyticsGraphSource(payload));
	};

export const setBusinessDashboardAnalyticsFilterListingTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardAnalyticsFilterListingType(payload));
	};

export const setBusinessDashboardAnalyticsFilterListingIdThunk =
	(payload: string | ObjectId): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardAnalyticsFilterListingId(payload));
	};

export const setBusinessDashboardAnalyticsFilterGraphTypeYThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardAnalyticsFilterGraphTypeY(payload));
	};

export const setBusinessDashboardAnalyticsFilterGraphDurationXThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardAnalyticsFilterGraphDurationX(payload));
	};

export const setBusinessDashboardAnalyticsFilterStartDateXThunk =
	(payload: string | Date): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardAnalyticsFilterStartDateX(payload));
	};

export const setBusinessDashboardAnalyticsFilterEndDateXThunk =
	(payload: string | Date): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardAnalyticsFilterEndDateX(payload));
	};

export const setBusinessDashboardUserViewTabValueThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardUserViewTabValue(payload));
	};

export const FetchBusinessDashboardAnalyticsProfileViewsThunk =
	(payload: { business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.FetchBusinessDashboardAnalyticsProfileViews(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/analytics/fetch-profile-views", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const FetchBusinessDashboardAnalyticsLeadsThisWeekThunk =
	(payload: { business_profile_id: string | ObjectId; listing_type: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.FetchBusinessDashboardAnalyticsLeadsThisWeek(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/analytics/fetch-leads-this-week", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const FetchBusinessDashboardAnalyticsListingViewsThisWeekThunk =
	(payload: { business_profile_id: string | ObjectId; listing_type: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.FetchBusinessDashboardAnalyticsListingViewsThisWeek(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/analytics/fetch-listing-views-this-week", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const FetchBusinessDashboardAnalyticsMostViewedListingThunk =
	(payload: { business_profile_id: string | ObjectId; listing_type: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.FetchBusinessDashboardAnalyticsMostViewedListing(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/analytics/fetch-most-viewed-listing", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const FetchBusinessDashboardAnalyticsMostLeadsGeneratingListingThunk =
	(payload: { business_profile_id: string | ObjectId; listing_type: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.FetchBusinessDashboardAnalyticsMostLeadsGeneratingListing(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/analytics/fetch-most-leads-generated-listing",
						{
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const FetchBusinessDashboardAnalyticsMostSavedListingThunk =
	(payload: { business_profile_id: string | ObjectId; listing_type: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.FetchBusinessDashboardAnalyticsMostSavedListing(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/analytics/fetch-most-saved-listing", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const FetchBusinessDashboardAnalyticsGraphThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		listing_type: string;
		listing_id: string | ObjectId;
		graph_type_y: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.FetchBusinessDashboardAnalyticsGraph(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/analytics/fetch-analytics-graph", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const FetchBusinessDashboardAnalyticsGraphCustomDatesThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		listing_type: string;
		listing_id: string | ObjectId;
		graph_type_y: string;
		start_date: string | Date;
		end_date: string | Date;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.FetchBusinessDashboardAnalyticsGraphCustomDates(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/analytics/fetch-custom-date-graph", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const CreateNewSharesThunk =
	(payload: {
		entity_type: string;
		entity_id: string | ObjectId;
		shared_through: string;
		entity_created_by: {
			is_business_profile: boolean;
			user_id: string | ObjectId;
			business_profile_id: string | ObjectId;
		};
		is_guest: boolean;
		logged_user: {
			user_id: string | ObjectId;
			business_profile_id: string | ObjectId;
			is_business_profile: boolean;
		} | null;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CreateNewShares(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "create-shares", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchSingleUserSavedItemsThunk =
	(payload: { user_id: string | ObjectId; listing_type: string; page: number }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleUserSavedItems(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/fetch-users-saved-items", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setUserSavedItemsFilterListingTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setUserSavedItemsFilterListingType(payload));
	};

export const setUserSavedItemsTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setUserSavedItemsTriggerNewSearch(payload));
	};

export const setUserSavedItemsInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setUserSavedItemsInitialLoadDone(payload));
	};

export const setUserSavedItemsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setUserSavedItemsLoading(payload));
	};

export const setUserSavedItemsFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setUserSavedItemsFilterPageNumber(payload));
	};

export const setLoginFlowGlobalStateModalOpenThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateModalOpen(payload));
	};

export const setLoginFlowGlobalStateUserCountryCodeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUserCountryCode(payload));
	};

export const setLoginFlowGlobalStateUserPhoneThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUserPhone(payload));
	};

export const setLoginFlowGlobalStateUserOTPThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUserOTP(payload));
	};

export const setLoginFlowGlobalStateUserIsSignUpThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUserIsSignUp(payload));
	};

export const setLoginFlowGlobalStateUserIsSignInThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUserIsSignIn(payload));
	};

export const setLoginFlowGlobalStateUserIsSignedInThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUserIsSignedIn(payload));
	};

export const setLoginFlowGlobalStateUserUserIdThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUserUserId(payload));
	};

export const setLoginFlowGlobalStateUserHasReferralCodeThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUserHasReferralCode(payload));
	};

export const setLoginFlowGlobalStateUserReferralCodeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUserReferralCode(payload));
	};

export const setLoginFlowGlobalStateStageTriggerSourceThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateStageTriggerSource(payload));
	};

export const setLoginFlowGlobalStateActionCallbackUrlThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateActionCallbackUrl(payload));
	};

export const setLoginFlowGlobalStateActionSignInThunk =
	(payload: (session: any) => void): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateActionSignIn(payload));
	};

export const setLoginFlowGlobalStateActionSignUpThunk =
	(payload: (session: any) => void): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateActionSignUp(payload));
	};

export const setLoginFlowGlobalStateActionSignOutThunk =
	(payload: () => void): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateActionSignOut(payload));
	};

export const setLoginFlowGlobalStateUiTypeThunk =
	(payload: "modal" | "redirect"): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUiType(payload));
	};

export const setLoginFlowGlobalStateUiSnackbarOpenThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUiSnackbarOpen(payload));
	};

export const setLoginFlowGlobalStateUiSnackbarMessageThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUiSnackbarMessage(payload));
	};

export const setLoginFlowGlobalStateUiSnackbarTypeThunk =
	(payload: "info" | "warning" | "success" | "error"): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUiSnackbarType(payload));
	};

export const setLoginFlowGlobalStateUserFirstNameThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUserFirstName(payload));
	};

export const setLoginFlowGlobalStateUserLastNameThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUserLastName(payload));
	};

export const setLoginFlowGlobalStateUserDisplayNameThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateUserDisplayName(payload));
	};

export const setLoginFlowGlobalStateReferrerFirstNameThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateReferrerFirstName(payload));
	};

export const setLoginFlowGlobalStateReferrerLastNameThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateReferrerLastName(payload));
	};

export const setLoginFlowGlobalStateReferrerDisplayNameThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateReferrerDisplayName(payload));
	};

export const setLoginFlowGlobalStateReferrerCountryCodeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateReferrerCountryCode(payload));
	};

export const setLoginFlowGlobalStateReferrerPhoneThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setLoginFlowGlobalStateReferrerPhone(payload));
	};

export const setUsersSettingsCurrentScreenThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setUsersSettingsCurrentScreen(payload));
	};

/* Chat Thunks */

export const setOpenChatModalThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setOpenChatModal(payload));
	};

export const setChatScreenThunk =
	(payload: string): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setChatScreen(payload));
	};

export const setChatListTabThunk =
	(payload: string): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setChatListTab(payload));
	};

export const setChatListTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setChatListTriggerNewSearch(payload));
	};

export const setChatListInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setChatListInitialLoadDone(payload));
	};

export const setChatListFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setChatListFilterPageNumber(payload));
	};

export const setBroadcastListTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBroadcastListTriggerNewSearch(payload));
	};

export const setBroadcastListInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBroadcastListInitialLoadDone(payload));
	};

export const setBroadcastListLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBroadcastListLoading(payload));
	};

export const setBroadcastListFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBroadcastListFilterPageNumber(payload));
	};

export const setChatMessagesTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setChatMessagesTriggerNewSearch(payload));
	};

export const setChatMessagesInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setChatMessagesInitialLoadDone(payload));
	};

export const setChatMessagesLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setChatMessagesLoading(payload));
	};

export const setChatMessagesFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setChatMessagesFilterPageNumber(payload));
	};

/*

* The chat list and the Requests list, one page at a time (chat audit I7, 17 Oct 2026). A page-1
* load replaces its list, so the answer to any request sent before the newest page-1 load is stale
* and is dropped: typing a search used to let an older, slower answer land last and overwrite the
* newer one. Each thunk tells its caller how it went ({ ok, stale }) so the list can show a loader,
* an error with Try again, or carry on. A network failure resets the list's loading flag.

*/

let chatListRequestSeq: number = 0;
let chatListNewestPageOne: number = 0;
let broadcastListRequestSeq: number = 0;
let broadcastListNewestPageOne: number = 0;

export const fetchUserChatsThunk =
	(payload: {
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		page: number;
		sessionId: string | ObjectId;
		search_term: string | undefined;
		/* A chip, narrowed by the server before paging (lib/chat/userChatListCore.ts `filter`); absent for All. */
		filter?: "unread" | "requirements" | "broadcasts";
	}): AppThunk<Promise<{ ok: boolean; stale: boolean }>> =>
	async (dispatch) => {
		const seq: number = ++chatListRequestSeq;
		if (payload.page === 1) chatListNewestPageOne = seq;
		try {
			const response = await fetch(
				process.env.PRODUCTION_API_URL +
					"chat/fetch-user-chats?search_term=" +
					encodeURIComponent(payload.search_term || ""),
				{
					method: "POST",
					cache: "no-store",
					body: JSON.stringify(payload),
				},
			);
			const result = await response.json().catch(() => null);
			if (seq < chatListNewestPageOne) return { ok: true, stale: true };
			dispatch(globalSlice.actions.fetchUserChats(result));
			return { ok: response.ok && Array.isArray(result?.payload?.chats), stale: false };
		} catch {
			if (seq < chatListNewestPageOne) return { ok: true, stale: true };
			dispatch(globalSlice.actions.fetchUserChats(null as any));
			return { ok: false, stale: false };
		}
	};

export const createNewBroadcastThunk =
	(payload: {
		source: { url: string; component: string };
		creator_is_business_profile: boolean;
		creator_user_id: string | ObjectId;
		creator_business_profile_id: string | ObjectId;
		property_id: string | ObjectId;
		no_of_agents: number;
		commission_offered: number;
		notes: string;
		property_status: string;
		sessionId: string | ObjectId;
		profile_context: StateInterface["profile_context"];
		/* The verified payment this broadcast spends, while broadcast payments are on. */
		razorpay_payment_id?: string;
		/* The draft being published, in place (Continue editing on a draft card). */
		broadcast_id?: string;
		/* "Pick a location for your broadcast" (paid flow, 3 Nov 2026): stored as intent by the server. */
		target_location?: { scope: "near_by"; latitude?: number; longitude?: number; radius_km?: number } | { scope: "entire_city"; city: string };
	}): AppThunk<Promise<{ ok: boolean; error: string | null }>> =>
	async (dispatch) => {
		try {
			const result = await (
				await fetch(process.env.PRODUCTION_API_URL + "broadcast/create-new-broadcast", {
					method: "POST",
					cache: "no-store",
					body: JSON.stringify(payload),
				})
			).json();
			dispatch(globalSlice.actions.createNewBroadcast(result));
			return { ok: result?.status === "success", error: result?.error || null };
		} catch {
			return { ok: false, error: "Network error" };
		}
	};

/* Saves the Create Broadcast dialog as a draft (closed part-way), or updates the draft it was resumed
   from (`broadcast_id`). The same route as a send, with `save_as_draft`; nothing in the store changes. */
export const saveBroadcastDraftThunk =
	(payload: {
		source: { url: string; component: string };
		creator_is_business_profile: boolean;
		creator_user_id: string | ObjectId;
		creator_business_profile_id: string | ObjectId;
		property_id: string | ObjectId;
		no_of_agents: number;
		commission_offered: number;
		notes: string;
		property_status: string;
		broadcast_id?: string;
	}): AppThunk<Promise<{ ok: boolean; broadcast_id: string | null }>> =>
	async () => {
		try {
			const result = await (
				await fetch(process.env.PRODUCTION_API_URL + "broadcast/create-new-broadcast", {
					method: "POST",
					cache: "no-store",
					body: JSON.stringify({ ...payload, save_as_draft: true }),
				})
			).json();
			return {
				ok: result?.status === "success",
				broadcast_id: result?.payload?.broadcast_id ? String(result.payload.broadcast_id) : null,
			};
		} catch {
			return { ok: false, broadcast_id: null };
		}
	};

/* Discards a broadcast draft (soft delete; drafts only). */
export const discardBroadcastDraftThunk =
	(payload: { broadcast_id: string; business_profile_id: string | ObjectId }): AppThunk<Promise<{ ok: boolean }>> =>
	async () => {
		try {
			const result = await (
				await fetch(process.env.PRODUCTION_API_URL + "broadcast/discard-broadcast-draft", {
					method: "POST",
					cache: "no-store",
					body: JSON.stringify(payload),
				})
			).json();
			return { ok: result?.status === "success" };
		} catch {
			return { ok: false };
		}
	};

export const fetchReceivedBroadcastsThunk =
	(payload: {
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		page: number;
		sessionId: string | ObjectId;
	}): AppThunk<Promise<{ ok: boolean; stale: boolean }>> =>
	async (dispatch) => {
		// The same rules as fetchUserChatsThunk above.
		const seq: number = ++broadcastListRequestSeq;
		if (payload.page === 1) broadcastListNewestPageOne = seq;
		try {
			const response = await fetch(process.env.PRODUCTION_API_URL + "broadcast/fetch-received-broadcasts", {
				method: "POST",
				cache: "no-store",
				body: JSON.stringify(payload),
			});
			const result = await response.json().catch(() => null);
			if (seq < broadcastListNewestPageOne) return { ok: true, stale: true };
			dispatch(globalSlice.actions.fetchReceivedBroadcasts(result));
			return { ok: response.ok && Array.isArray(result?.payload?.broadcasts), stale: false };
		} catch {
			if (seq < broadcastListNewestPageOne) return { ok: true, stale: true };
			dispatch(globalSlice.actions.fetchReceivedBroadcasts(null as any));
			return { ok: false, stale: false };
		}
	};

export const removeReceivedBroadcastFromListThunk =
	(payload: { broadcast_id: string | ObjectId; invite_status: string }): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.removeReceivedBroadcastFromList(payload));
	};

export const acceptedBroadcastThunk =
	(payload: { broadcast_id: string | ObjectId; chat_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.acceptedBroadcast(payload));
	};

export const fetchSentBroadcastsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		page: number;
		sort_field: string;
		sort_order: number;
		/* "all" | "active" (Sent) | "in_review" | "draft"; absent is "all". */
		status_filter?: string;
		/* Narrows to broadcasts whose listing title has this text; absent or "" is no search. */
		search_text?: string;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSentBroadcasts(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "broadcast/fetch-sent-broadcasts", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchChatMessagesThunk =
	(payload: {
		chat_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		page: number;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchChatMessages(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "chat/fetch-messages", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setCurrentChatThunk =
	(payload: {
		chat_id: string | ObjectId;
		chat_type: string;
		chat_title: string;
		chat_description: string;
		chat_dp: ProfilePicture;
		chat_is_broadcast: boolean;
		chat_notification_settings: NotificationSettingInterface;
		chat_participants: Array<Participants>;
		chat_created_at: Date | string;
		chat_source_collection: string | undefined;
		chat_last_message?: LastMessageInterface | undefined;
		chat_broadcast_id: string | ObjectId | undefined;
		property_id?: string | ObjectId | undefined;
		invite_status?: string | undefined;
		has_unread_message?: boolean;
		is_request?: boolean;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setCurrentChat(payload));
	};

/*

* Accepts a chat request (chat/accept-chat-request, chat audit 2.2). On success the composer opens
* (chatRequestAccepted). The caller gets { ok, error }: `error` is a server line meant for the user
* (the other side is no longer available, or blocked), never anything technical.

*/

/* Freezes (or unfreezes) the open chat's composer after a block made from its header. */
/* Starts (or, with null, stops) replying to a message in the open chat (chat audit I13). */
export const setChatReplyToThunk =
	(payload: { message_id: string; sender_name: string; body: string } | null): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setChatReplyTo(payload));
	};

/* The chat list's total with no search term (18 Oct 2026). */
export const setChatListAllCountThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setChatListAllCount(payload));
	};

/* The other side of [chat_ids] started or stopped typing (18 Oct 2026, a `typing` frame). */
export const setChatTypingThunk =
	(payload: { chat_ids: Array<string>; typing: boolean }): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setChatTyping(payload));
	};

/* The message the composer edits (18 Oct 2026), or null to stop editing. */
export const setChatEditingThunk =
	(payload: { message_id: string; body: string } | null): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setChatEditing(payload));
	};

/*

* Saves an edit of one of the signed-in person's own text messages (18 Oct 2026, chat/edit-message,
* lib/chat/editMessage.ts: a text message, by its sender, within 12 hours). On success the bubble
* takes the stored text with "Edited"; a refusal the server words for the user comes back as `error`.

*/
export const editChatMessageThunk =
	(payload: {
		chat_id: string | ObjectId;
		message_id: string;
		message_body: string;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
	}): AppThunk<Promise<{ ok: boolean; error: string | null }>> =>
	async (dispatch) => {
		try {
			const response = await fetch(process.env.PRODUCTION_API_URL + "chat/edit-message", {
				method: "POST",
				cache: "no-store",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			});
			const result = await response.json().catch(() => null);
			if (response.ok && result?.status === "success") {
				dispatch(
					globalSlice.actions.applyMessageEdit({
						message_id: payload.message_id,
						body: String(result.payload?.body ?? payload.message_body),
					}),
				);
				return { ok: true, error: null };
			}
			const words: Array<string> = [
				"You can only edit your own messages",
				"This message can't be edited",
				"This message can no longer be edited",
				"You can't message this profile",
				"This profile is no longer available",
			];
			return { ok: false, error: words.includes(result?.error) ? result.error : null };
		} catch {
			return { ok: false, error: null };
		}
	};

/* The open chat's own settings changed from its header (18 Oct 2026): mute, block, unblock. */
export const setCurrentChatSettingsThunk =
	(payload: { is_muted?: boolean; is_blocked?: boolean; my_block_id?: string }): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setCurrentChatSettings(payload));
	};

/*

* Mutes or unmutes the open conversation for the signed-in person only (chat/mute-chat,
* lib/chat/chatSettings.ts, 18 Oct 2026). On success the chat's `is_muted` follows; the caller gets
* whether it worked.

*/
export const muteChatThunk =
	(payload: {
		chat_id: string | ObjectId;
		muted: boolean;
		is_business_profile: boolean;
		business_profile_id: string | ObjectId;
	}): AppThunk<Promise<boolean>> =>
	async (dispatch) => {
		try {
			const response = await fetch(process.env.PRODUCTION_API_URL + "chat/mute-chat", {
				method: "POST",
				cache: "no-store",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			});
			const result = await response.json().catch(() => null);
			const ok: boolean = response.ok && result !== null && result.status === "success";
			if (ok) dispatch(globalSlice.actions.setCurrentChatSettings({ is_muted: result.payload?.is_muted === true }));
			return ok;
		} catch {
			return false;
		}
	};

/*

* Sets the signed-in person's reaction on a message, or takes it away with "" (chat/react-to-message,
* lib/chat/reactions.ts, 18 Oct 2026). The bubble shows it at once ([optimistic]) and settles on the
* server's summary; on failure it goes back. The caller gets { ok, error }: `error` is a server line
* meant for the user, never anything technical.

*/
export const reactToChatMessageThunk =
	(payload: {
		chat_id: string | ObjectId;
		message_id: string;
		emoji: string;
		is_business_profile: boolean;
		business_profile_id: string | ObjectId;
		optimistic: Array<{ emoji: string; count: number; by_viewer: boolean }>;
		previous: Array<{ emoji: string; count: number; by_viewer: boolean }>;
	}): AppThunk<Promise<{ ok: boolean; error: string | null }>> =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setMessageReactions({ message_id: payload.message_id, reaction_summary: payload.optimistic }));
		try {
			const { optimistic, previous, ...body } = payload;
			const response = await fetch(process.env.PRODUCTION_API_URL + "chat/react-to-message", {
				method: "POST",
				cache: "no-store",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(body),
			});
			const result = await response.json().catch(() => null);
			if (response.ok && result?.status === "success") {
				dispatch(
					globalSlice.actions.setMessageReactions({
						message_id: payload.message_id,
						reaction_summary: Array.isArray(result.payload?.reaction_summary) ? result.payload.reaction_summary : [],
					}),
				);
				return { ok: true, error: null };
			}
			dispatch(globalSlice.actions.setMessageReactions({ message_id: payload.message_id, reaction_summary: payload.previous }));
			const known: Array<string> = [
				"Accept the chat request to reply",
				"This message is no longer available",
				"This profile is no longer available",
				"You can't message this profile",
			];
			return { ok: false, error: typeof result?.error === "string" && known.includes(result.error) ? result.error : null };
		} catch {
			dispatch(globalSlice.actions.setMessageReactions({ message_id: payload.message_id, reaction_summary: payload.previous }));
			return { ok: false, error: null };
		}
	};

/*

* A `message-deleted` frame for the open chat (a hint only, 18 Oct 2026): re-reads that message and
* puts the server's version in the thread, so a message deleted for everyone turns into its
* tombstone at once (it stayed until the chat was reopened). Marks nothing read.

*/
export const refreshChatMessageThunk =
	(payload: {
		chat_id: string | ObjectId;
		message_id: string;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		try {
			const response = await fetch(process.env.PRODUCTION_API_URL + "chat/fetch-realtime-new-chat-message", {
				method: "POST",
				headers: { "Content-Type": "text/plain" },
				body: JSON.stringify({ ...payload, current_screen: "chat_list" }),
			});
			const result = await response.json().catch(() => null);
			const message: any = result?.status === "success" ? result.payload?.message : null;
			if (message && message._id) dispatch(globalSlice.actions.replaceChatMessage(message));
		} catch {
			// A hint only: the next load shows it anyway.
		}
	};

/*

* The chat list's unread counts, previews and badge, re-read from page 1 (18 Oct 2026): a chat read
* on another device or by a teammate (a `chat-read` frame for a chat that is not open), or a last
* message deleted for everyone. Rows change in place (syncChatListUnread); nothing reloads.

*/
export const refreshChatListUnreadThunk =
	(payload: {
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		try {
			const response = await fetch(process.env.PRODUCTION_API_URL + "chat/fetch-user-chats?search_term=", {
				method: "POST",
				cache: "no-store",
				body: JSON.stringify({ ...payload, page: 1, search_term: "" }),
			});
			const result = await response.json().catch(() => null);
			if (result) dispatch(globalSlice.actions.syncChatListUnread(result));
		} catch {
			// A hint only: the next list load shows it anyway.
		}
	};

/*

* A `chat-read` / `chat-delivered` frame for the open chat (a hint only, 18 Oct 2026): re-reads the
* newest page's receipts and moves the bubbles' ticks forward, without touching the thread otherwise.

*/
export const refreshChatReceiptsThunk =
	(payload: {
		chat_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		try {
			const response = await fetch(process.env.PRODUCTION_API_URL + "chat/fetch-messages", {
				method: "POST",
				cache: "no-store",
				headers: { "Content-Type": "text/plain" },
				body: JSON.stringify({ ...payload, page: 1 }),
			});
			const result = await response.json().catch(() => null);
			if (result?.status !== "success") return;
			dispatch(
				globalSlice.actions.setMessageReceipts({
					served: (result.payload?.messages || []).map((m: any) => ({
						_id: String(m._id),
						is_read: m.is_read === true,
						is_delivered: m.is_delivered === true,
					})),
					viewer: {
						is_business_profile: payload.is_business_profile === true,
						user_id: String(payload.user_id),
						business_profile_id: String(payload.business_profile_id),
					},
				}),
			);
		} catch {
			// A hint only: the next load shows it anyway.
		}
	};

/*

* A `message-reaction` frame (a hint only): re-reads that one message for its reactions, without
* marking anything read (current_screen "chat_list") and without touching the chat list.

*/
export const refreshMessageReactionsThunk =
	(payload: {
		chat_id: string | ObjectId;
		message_id: string;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		try {
			const response = await fetch(process.env.PRODUCTION_API_URL + "chat/fetch-realtime-new-chat-message", {
				method: "POST",
				headers: { "Content-Type": "text/plain" },
				body: JSON.stringify({ ...payload, current_screen: "chat_list" }),
			});
			const result = await response.json().catch(() => null);
			const message: any = result?.status === "success" ? result.payload?.message : null;
			if (message) {
				dispatch(
					globalSlice.actions.setMessageReactions({
						message_id: String(message._id),
						reaction_summary: Array.isArray(message.reaction_summary) ? message.reaction_summary : [],
					}),
				);
			}
		} catch {
			// A hint only: the next load shows it anyway.
		}
	};

export const acceptChatRequestThunk =
	(payload: {
		chat_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
	}): AppThunk<Promise<{ ok: boolean; error: string | null }>> =>
	async (dispatch) => {
		try {
			const response = await fetch(process.env.PRODUCTION_API_URL + "chat/accept-chat-request", {
				method: "POST",
				cache: "no-store",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(payload),
			});
			const result = await response.json().catch(() => null);
			const ok: boolean = response.ok && result !== null && result.status === "success";
			if (ok) {
				dispatch(globalSlice.actions.chatRequestAccepted({ chat_id: String(payload.chat_id) }));
				return { ok: true, error: null };
			}
			const known: Array<string> = ["You can't message this profile"];
			const error: string | null =
				typeof result?.error === "string" && (known.includes(result.error) || /no longer available/i.test(result.error))
					? result.error
					: null;
			return { ok: false, error };
		} catch {
			return { ok: false, error: null };
		}
	};

/*

* Declines a chat request (19 Oct 2026, chat/decline-chat-request, lib/chat/declineRequestCore.ts):
* the request leaves the viewer's list for good and nobody is told. On success its row leaves the
* list and the thread closes (chatRequestDeclined). The caller gets whether it worked; a request that
* was no longer pending (`declined: false`) still counts, so a double click is harmless.

*/
export const declineChatRequestThunk =
	(payload: {
		chat_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
	}): AppThunk<Promise<boolean>> =>
	async (dispatch) => {
		try {
			const response = await fetch(process.env.PRODUCTION_API_URL + "chat/decline-chat-request", {
				method: "POST",
				cache: "no-store",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(payload),
			});
			const result = await response.json().catch(() => null);
			const ok: boolean = response.ok && result !== null && result.status === "success";
			if (ok) dispatch(globalSlice.actions.chatRequestDeclined({ chat_id: String(payload.chat_id) }));
			return ok;
		} catch {
			return false;
		}
	};

export const sendChatMessageThunk =
	(payload: {
		chat_id: ObjectId | string;
		message_id: ObjectId | string;
		sender_is_business_profile: boolean;
		sender_user_id: ObjectId | string;
		sender_business_profile_id: ObjectId | string;
		message_body: string;
		attachments: Array<{
			file_id: ObjectId | string;
			mime_type: string;
		}>;
		sessionId: ObjectId | string;
		/* The quoted message (chat audit I13); the server stores it as the body's reply tail. */
		reply_to?: string;
		/* A place to share instead of text (decision D10, lib/chat/locationContact.ts); sent alone. The
		   address line (19 Oct 2026) goes under the name on the card. */
		location?: { latitude: number; longitude: number; title: string; address?: string };
		/* Share the acting business's own contact card; sent alone, businesses only. */
		share_contact?: boolean;
		/* Listings shared with the words (chat schema 4.4), e.g. "I'm interested in these properties" (gap A7). */
		shared_entity?: Array<{ entity_type: "property"; entity_id: string }>;
	}): AppThunk<Promise<{ ok: boolean; error?: string }>> =>
	async (dispatch) => {
		/* The caller learns whether the message was stored (chat audit F3, 17 Oct 2026), so a photo
		   send can say it failed instead of spinning forever. The reducer still gets every answer
		   the server sent; a network failure dispatches nothing and answers { ok: false }. */
		try {
			const response = await fetch(process.env.PRODUCTION_API_URL + "chat/send-message", {
				method: "POST",
				cache: "no-store",
				headers: {
					"Content-Type": "text/plain",
				},
				body: JSON.stringify(payload),
			});
			const result = await response.json().catch(() => null);
			if (result) {
				dispatch(globalSlice.actions.sendChatMessage(result));
			}
			const ok: boolean = response.ok && result !== null && result.status === "success";
			// The words a location or contact refusal carries (lib/chat/locationContact.ts) are meant
			// for the user; anything else stays generic.
			const knownErrors: Array<string> = [
				"Send a location or contact on its own",
				"Couldn't share that location. Please try again",
				"Only a business can share its contact",
				"Add a phone number to your business profile to share it",
			];
			return { ok, error: !ok && knownErrors.includes(String(result?.error || "")) ? String(result.error) : undefined };
		} catch {
			return { ok: false };
		}
	};

export const deleteChatThunk =
	(payload: {
		chat_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		sessionId: ObjectId | string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.deleteChat(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "chat/delete-chat", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

/*

* Deletes one message of the open conversation, for everyone or for this viewer only. The
* identity is the acting profile (profile_context); the server re-checks it. The thread is
* changed only after the server says yes, and the caller gets { ok, http_status } back to
* show its own short message on a refusal (403: not the sender's side, 409: past the
* delete-for-everyone window). Never the server's text.

*/

export const deleteChatMessageThunk =
	(payload: {
		delete_for: "everyone" | "me";
		chat_id: string | ObjectId;
		message_id: string | ObjectId;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
	}): AppThunk<Promise<{ ok: boolean; http_status: number }>> =>
	async (dispatch) => {
		const { delete_for, ...body } = payload;
		try {
			const response = await fetch(
				process.env.PRODUCTION_API_URL +
					(delete_for === "everyone" ? "chat/delete-message-for-everyone" : "chat/delete-message-for-me"),
				{
					method: "POST",
					cache: "no-store",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify(body),
				},
			);
			const result = await response.json().catch(() => null);
			const ok: boolean = response.ok && result !== null && result.status === "success";
			if (ok) {
				dispatch(globalSlice.actions.deleteChatMessage({ message_id: payload.message_id, delete_for: delete_for }));
			}
			return { ok: ok, http_status: response.status };
		} catch (error) {
			return { ok: false, http_status: 0 };
		}
	};

export const setBusinessDashboardPropertyBroadcastTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardPropertyBroadcastTriggerNewSearch(payload));
	};

export const setBusinessDashboardPropertyBroadcastInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardPropertyBroadcastInitialLoadDone(payload));
	};

export const setBusinessDashboardPropertyBroadcastsFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardPropertyBroadcastsFilterPageNumber(payload));
	};

export const fetchRealtimeNewChatMessageThunk =
	(payload: {
		sessionId: string;
		chat_id: string | ObjectId;
		message_id: ObjectId | string;
		current_screen: string;
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchRealtimeNewChatMessage(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "chat/fetch-realtime-new-chat-message", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setBroadcastFormThunk =
	(payload: {
		commission_offered: number;
		no_of_agents: number;
		notes: string;
		open_to_negotiation?: boolean;
	}): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBroadcastForm(payload));
	};

export const fetchUserPropertiesThunk =
	(payload: {
		is_business_profile: boolean;
		user_id: string | ObjectId;
		business_profile_id: string | ObjectId;
		page: number;
		sessionId: string | ObjectId;
		search_term: string | undefined;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchUserChats(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "search/autocomplete-user-properties" + (payload.search_term || ""),
						{
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const fetchFeatureDiscoveryTrackerThunk =
	(payload: { profile_id: string | ObjectId; is_business_profile: boolean }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchFeatureDiscoveryTracker(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "feature-discovery-tracker/fetch-feature-discovery-tracker", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateFeatureDiscoveryTrackerThunk =
	(payload: { profile_id: string | ObjectId; is_business_profile: boolean; broadcast_skipped?: boolean }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateFeatureDiscoveryTracker(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "feature-discovery-tracker/update-feature-discovery-tracker", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setBusinessDashboardAiBoostFormBudgetThunk =
	(payload: { expected_qualified_leads: number; total_amount: number; duration: number }): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBusinessDashboardAiBoostFormBudget(payload));
	};

export const setBusinessDashboardAiBoostFormCustomBudgetThunk =
	(payload: {
		name: string;
		phone: {
			phone_number: string;
			country_code: string;
		};
		budget: number;
		contact_preferences: string;
		source: {
			url: string;
			component: string;
			form_type: string;
		};
		notes?: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.setBusinessDashboardAiBoostFormCustomBudget(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/ai-boost/store-aiboosts-b2b-leads", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setAiBoostFormOfflinePaymentThunk =
	(payload: {
		name: string;
		phone: {
			phone_number: string;
			country_code: string;
		};
		source: { url: string; component: string; form_type: string };
		notes?: string;
		renewal?: boolean;
		budget?: number;
		contact_preferences?: string;
		properties_to_boost?: Array<{
			property_id: string | ObjectId;
			property_name: string;
			exists: boolean;
		}>;
		business_profile_id?: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.setAiBoostFormOfflinePayment(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/ai-boost/store-aiboosts-b2b-leads", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setAiBoostFormPropertyDetailsThunk =
	(payload: { property_data: PropertyInterface }): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setAiBoostFormPropertyDetails(payload));
	};

export const setAiBoostFormAdCreativeImageThunk =
	(payload: { website_url: string; additional_notes: string }): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setAiBoostFormAdCreativeImage(payload));
	};

export const setAiBoostFormAdCreativeReferenceImagesThunk =
	(
		payload: Array<{
			file_id: ObjectId | string;
			mime_type: string;
			preview: string;
		}>,
	): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setAiBoostFormAdCreativeReferenceImages(payload));
	};

export const setAiBoostFormAdCreativeBrandLogoThunk =
	(payload: { file_id: ObjectId | string; mime_type: string; preview: string }): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setAiBoostFormAdCreativeBrandLogo(payload));
	};

export const fetchAiMetaBoostedListingsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		filter_status: string;
		sessionId: string | ObjectId;
		page_number: number;
		sort_field?: string;
		sort_order?: number;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAiMetaBoostedListings(
				await (
					await await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/ai-boost/fetch-ai-meta-boosted-listings",
						{
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const setAiMetaBoostedListingsTriggerNewSearchThunk =
	(payload: { trigger_new_search: boolean; filter_status: string }): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setAiMetaBoostedListingsTriggerNewSearch(payload));
	};

export const setAiMetaBoostedListingsInitialLoadDoneThunk =
	(payload: { initial_load_done: boolean; filter_status: string }): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setAiMetaBoostedListingsInitialLoadDone(payload));
	};

export const setAiMetaBoostedListingsFilterStatusThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setAiMetaBoostedListingsFilterStatus(payload));
	};

export const setAiMetaBoostedListingsFilterSortFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setAiMetaBoostedListingsFilterSortField(payload));
	};

export const setAiMetaBoostedListingsFilterSortOrderThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setAiMetaBoostedListingsFilterSortOrder(payload));
	};

export const fetchRecomendedPropertiesToBoostThunk =
	(payload: { business_profile_id: string | ObjectId; page_number: number; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchRecomendedPropertiesToBoost(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/ai-boost/fetch-recommended-properties", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
export const fetchRecomendedPropertiesToBoost = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_ai_boosts_response.recommended_properties_to_boost.recommended_properties =
			action.payload.payload.recommended_properties;
		state.business_dashboard.search_ai_boosts_response.recommended_properties_to_boost.recommended_properties_count =
			action.payload.payload.recommended_properties_count;
		state.business_dashboard.search_ai_boosts_response.recommended_properties_to_boost.recommended_properties_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};
export const setRecommendedPropertiesTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setRecommendedPropertiesTriggerNewSearch(payload));
	};

export const setRecommendedPropertiesInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setRecommendedPropertiesInitialLoadDone(payload));
	};

export const deleteDesktopLeadsThunk =
	(payload: { lead_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.deleteDesktopLeads(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/lead/delete-cta-lead", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const deleteMobileLeadsThunk =
	(payload: { lead_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.deleteMobileLeads(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "/lead/delete-cta-lead", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchAiBoostBannerCardDetailsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.fetchAiBoostBannerCardDetailsLoading(payload));
	};

export const setAiBoostDetailsToNullThunk = (): AppThunk => (dispatch) => {
	dispatch(globalSlice.actions.setAiBoostDetailsToNull());
};

export const fetchAiBoostBannerCardDetailsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		property_id: string | ObjectId;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAiBoostBannerCardDetails(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/ai-boosts-details/fetch-banner-card-details",
						{
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const fetchAiBoostAllBoostListsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.fetchAiBoostAllBoostListsLoading(payload));
	};

export const fetchAiBoostAllBoostListsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		property_id: string | ObjectId;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAiBoostAllBoostLists(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/ai-boosts-details/fetch-all-boost-lists", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchGoogleCampaignsListsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		property_id: string | ObjectId;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchGoogleCampaignsLists(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/ai-boosts-details/fetch-google-campaigns-lists",
						{
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const setMetaAiBoostDetailsSelectedCampaignThunk =
	(payload: string | ObjectId): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setMetaAiBoostDetailsSelectedCampaign(payload));
	};

export const fetchMetaAiBoostsQualifiedLeadsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		boost_ids: Array<string | ObjectId>;
		property_id: string | ObjectId;
		start_date: string | Date;
		end_date: string | Date;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchMetaAiBoostsQualifiedLeads(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/ai-boosts-details/fetch-all-qualified-leads",
						{
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const fetchMetaAiBoostsBudgetDetailsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		boost_ids: Array<string | ObjectId>;
		property_id: string | ObjectId;
		start_date: string | Date;
		end_date: string | Date;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		try {
			const response = await (
				await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/ai-boosts-details/fetch-budget-details", {
					method: "POST",
					cache: "no-store",
					body: JSON.stringify(payload),
				})
			).json();
			dispatch(globalSlice.actions.fetchMetaAiBoostsBudgetDetails(response));
		} catch (error) {
			console.error("[META_BUDGET_THUNK_ERROR]", error);
		}
	};

export const fetchMetaAiBoostsCampaignStatsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		boost_ids: Array<string | ObjectId>;
		property_id: string | ObjectId;
		start_date: string | Date;
		end_date: string | Date;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchMetaAiBoostsCampaignStats(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/ai-boosts-details/fetch-campaign-stats", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchGoogleAIBoostCampaignMetricsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		boost_ids: Array<string | ObjectId>;
		property_id: string | ObjectId;
		start_date: string | Date;
		end_date: string | Date;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		try {
			const response = await (
				await fetch(
					process.env.PRODUCTION_API_URL + "business-dashboard/ai-boosts-details/fetch-google-budget-details",
					{
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					},
				)
			).json();
			dispatch(globalSlice.actions.fetchGoogleAIBoostCampaignMetrics(response));
		} catch (error) {
			console.error("[GOOGLE_BUDGET_THUNK_ERROR]", error);
		}
	};

export const fetchGoogleAIBoostsQualifiedLeadsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		boost_ids: Array<string | ObjectId>;
		property_id: string | ObjectId;
		start_date: string | Date;
		end_date: string | Date;
		sessionId: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchGoogleAIBoostsQualifiedLeads(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/ai-boosts-details/fetch-google-qualified-leads",
						{
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const setFilterSelectedPlatformThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setFilterSelectedPlatform(payload));
	};

export const setMetaAiBoostAdPreviewsFilterPageNumberCountThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setMetaAiBoostAdPreviewsFilterPageNumberCount(payload));
	};

export const fetchMetaAiBoostAllAdIdsThunk =
	(payload: { boost_id: string | ObjectId; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchMetaAiBoostAllAdIds(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/ai-boosts-details/fetch-all-ad-ids", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchMetaAiBoostsAdCreativesThunk =
	(payload: { ad_id: string; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchMetaAiBoostsAdCreatives(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/ai-boosts-details/fetch-ad-creatives", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setMetaCreativesLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setMetaCreativesLoading(payload));
	};

export const setMetaAiBoostActivityLogsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setMetaAiBoostActivityLogsLoading(payload));
	};

export const fetchMetaAiBoostActivityLogdDataThunk =
	(payload: { boost_id: string | ObjectId; page_number: number; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchMetaAiBoostActivityLogdData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/ai-boosts-details/fetch-activity-logs", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const genetateAIBoostActivityLogsSummaryThunk =
	(payload: {
		boost_id: string | ObjectId;
		sessionId: string | ObjectId;
		business_profile_id: string | ObjectId;
		profile_context: StateInterface["profile_context"];
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.genetateAIBoostActivityLogsSummary(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/ai-boosts-details/generate-campaign-summary-ai",
						{
							method: "POST",
							cache: "no-store",
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const fetchHomepageDashboardStatsThunk =
	(payload: { business_profile_id: ObjectId | string; sessionId: ObjectId | string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchHomepageDashboardStats(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "home/fetch-dashboard-stats", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setSingleLeadDataThunk =
	(payload: StateInterface["business_dashboard"]["single_lead_details"]): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setSingleLeadData(payload));
	};

export const handleLeadsDetailsStatusThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		lead_id: string | ObjectId;
		lead_name: string;
		status: string;
		previous_lead_status: string;
		assigned_by: StateInterface["profile_context"];
		session_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.handleLeadsDetailsStatus(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/leads/update-lead-status", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const removejunkLeadFromListThunk =
	(payload: string | ObjectId): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.removejunkLeadFromList(payload));
	};

export const setLeadDetailsReminderThunk =
	(payload: {
		lead_id: string | ObjectId;
		trigger_reminder_at: Date | null;
		reminder_at: Date | null;
		title: string;
		created_at: Date | string;
		session_id: string | ObjectId;
		assigned_by: StateInterface["profile_context"];
		business_profile_id: string | ObjectId;
		lead_name: string;
		owner: boolean;
		// Multi-reminder support: present when editing an existing reminder in
		// place; omitted/null when adding a new one.
		reminder_id?: string | ObjectId | null;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.setLeadDetailsReminder(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/leads/set-leads-reminders", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const handleDeleteLeadReminderThunk =
	(payload: {
		lead_id: string | ObjectId;
		assigned_by: StateInterface["profile_context"];
		business_profile_id: string | ObjectId;
		lead_name: string;
		owner: boolean;
		// Multi-reminder support: which reminder to remove. Omitted falls back to
		// the API's legacy "delete the last one" behaviour.
		reminder_id?: string | ObjectId | null;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.handleDeleteLeadReminder(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/leads/delete-lead-reminder", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const addNewNoteToLeadActivityLogsThunk =
	(payload: {
		lead_id: string | ObjectId;
		lead_name: string;
		action: string;
		notes: string;
		action_performed_at: Date | string;
		assigned_by: StateInterface["profile_context"];
		session_id: string | ObjectId;
		business_profile_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.addNewNoteToLeadActivityLogs(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/leads/add-lead-note", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateLeadActivityLogsNoteThunk =
	(payload: {
		activity_logs: Array<{
			action: string;
			notes: string;
			status: String;
			assigned_to: {
				is_business_profile: Boolean;
				user_id: string | ObjectId;
				business_profile_id: string | ObjectId;
				title: String;
				phone: String;
			};
			action_performed_at: Date | string;
			created_by: {
				is_business_profile: boolean;
				user_id: ObjectId | string;
				business_profile_id: ObjectId | string;
			};
		}>;
		lead_id: string | ObjectId;
		lead_name: string;
		assigned_by: StateInterface["profile_context"];
		business_profile_id: string | ObjectId;
		previous_note: string;
		edited_note: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.addNewNoteToLeadActivityLogs(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/leads/update-lead-note", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const UpdateBroadcastReadStatusThunk =
	(payload: {
		_id: string | ObjectId | undefined;
		is_business_profile: boolean;
		business_profile_id: string | ObjectId;
		user_id: string | ObjectId;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateBroadcastReadStatus(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "broadcast/update-read", {
						method: "POST",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateBusinessDashboardLeadsAssignedToThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		lead_ids: Array<string | ObjectId>;
		lead_name: Array<string>;
		assigned_to: Array<string | ObjectId>;
		first_name: Array<string>;
		last_name: Array<string>;
		phone_number: Array<string>;
		assigned_by: BeegruUserProfileContext | null;
	}): AppThunk<Promise<any>> =>
	async (dispatch) => {
		/* Resolves to the route's reply, so a caller can put its own selection back when the
		   assignment is refused (status "failure", payload null). */
		const reply: any = await (
			await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/leads/leads-assigned-to", {
				method: "POST",
				cache: "no-store",
				headers: {
					"Content-Type": "text/plain",
				},
				body: JSON.stringify(payload),
			})
		)
			.json()
			.catch(() => ({ status: "failure", payload: null, missingFields: null, error: "Invalid reply" }));
		dispatch(globalSlice.actions.updateBusinessDashboardLeadsAssignedTo(reply));
		return reply;
	};
export const fetchBusinessDashboardTeamMembersThunk =
	(payload: { business_profile_id: string | ObjectId; search_text: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardTeamMembers(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/leads/search-admin", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchAiMetaActiveCampaignsThunk =
	(payload: { business_profile_id: string | ObjectId; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAiMetaActiveCampaigns(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/home/fetch-ai-meta-active-campaigns", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchHomeScreenBroadcastWidgetThunk =
	(payload: { business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchHomeScreenBroadcastWidget(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/home/fetch-broadcast-widget", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchTopAgentAreasThunk = (): AppThunk => async (dispatch) => {
	dispatch(
		globalSlice.actions.fetchTopAgentAreas(
			await (
				await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/home/fetch-top-agent-trends", {
					method: "POST",
					cache: "no-store",
					headers: {
						"Content-Type": "text/plain",
					},
				})
			).json(),
		),
	);
};

export const fetchPerformanceAnalyticsBusinessDashboardHomeWidgetThunk =
	(payload: { slug: string; business_profile_id: string | ObjectId; period: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchPerformanceAnalyticsBusinessDashboardHomeWidget(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/home/fetch-performance-analytics", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchHomePageStatsThunk =
	(payload: { business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchHomePageStats(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "home/fetch-stats", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchAllCampaignMetricsThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		boost_ids: Array<string | ObjectId>;
		property_id: string | ObjectId;
		start_date: string | Date;
		end_date: string | Date;
		sessionId: string | ObjectId;
		report_type: string;
		platform?: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchAllCampaignMetrics(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/ai-boosts-details/fetch-all-campaign-metrics",
						{
							method: "POST",
							cache: "no-store",
							headers: {
								"Content-Type": "text/plain",
							},
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const setMetricsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setMetricsLoading(payload));
	};

export const setBudgetAndLeadsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setBudgetAndLeadsLoading(payload));
	};
export const setSearchSortByThunk =
	(payload: StateInterface["search_sort_by"]): AppThunk =>
	async (dispatch) => {
		dispatch(globalSlice.actions.setSearchSortBy(payload));
	};

export const setNerSearchTermThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setNerSearchTerm(payload));
	};

export const setNerLastSearchedTermThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setNerLastSearchedTerm(payload));
	};

export const fetchBusinessDashboardListingsCountThunk =
	(payload: { business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardListingsCount(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/listings/fetch-listing-counts", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchClientCrmIntegrationWebhookThunk =
	(payload: { business_profile_id: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchClientCrmIntegrationWebhook(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/crm-integration/fetch-client-crm-integration-webhook",
						{
							method: "POST",
							cache: "no-store",
							headers: {
								"Content-Type": "text/plain",
							},
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const CreateCrmIntegrationWebhookThunk =
	(payload: {
		channel_id: String | ObjectId;
		business_profile_id: String | ObjectId;
		crm_name: string;
		api_method: String;
		webhook_url: String;
		payload_type: String;
		api_key: String;
		lead_data?: {
			email: String;
			channel_id: String;
			lead_id: String;
			name: String;
			phone: String;
			project_name: String;
			status: String;
			notes: String;
			lead_source: String;
			enquired_at: String;
			api_key: String;
		};
		headers: Array<{ key: String; value: String }>;
		is_enabled: Boolean;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.CreateCrmIntegrationWebhook(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/crm-integration/create-crm-integration-webhook",
						{
							method: "POST",
							cache: "no-store",
							headers: {
								"Content-Type": "text/plain",
							},
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const TestCrmIntegrationWebhookThunk =
	(payload: { business_profile_id: string | ObjectId; webhookUrl: string; apiMethod: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.TestCrmIntegrationWebhook(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/crm-integration/test-crm-integration-webhook",
						{
							method: "POST",
							cache: "no-store",
							headers: {
								"Content-Type": "text/plain",
							},
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const UpdateCrmIntegrationWebhookStatusThunk =
	(payload: {
		business_profile_id: string | ObjectId;
		is_enabled: boolean;
		sessionId: string | ObjectId;
		url?: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.UpdateCrmIntegrationWebhook(
				await (
					await fetch(
						process.env.PRODUCTION_API_URL + "business-dashboard/crm-integration/update-crm-integration-webhook-status",
						{
							method: "POST",
							cache: "no-store",
							headers: {
								"Content-Type": "text/plain",
							},
							body: JSON.stringify(payload),
						},
					)
				).json(),
			),
		);
	};

export const setCRMApiMethodThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMApiMethod(payload));
	};

export const setCRMWebhookURLThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMWebhookURL(payload));
	};

export const setCRMNameThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMName(payload));
	};

export const setChannelIdThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setChannelId(payload));
	};

export const setCRMEmailFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMEmailField(payload));
	};

export const setCRMChannelIDFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMChannelIDField(payload));
	};

export const setCRMWebhookApiKeyThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMWebhookApiKey(payload));
	};

export const setCRMHeadersKeyThunk =
	(payload: { index: number; newKey: string }): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMHeadersKey(payload));
	};

export const setCRMAddNewHeaderFieldThunk = (): AppThunk => (dispatch) => {
	dispatch(globalSlice.actions.setCRMAddNewHeaderField({ key: "", value: "" }));
};

export const setCRMRemoveHeaderFieldThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMRemoveHeaderField(payload));
	};

export const setCRMHeadersValueThunk =
	(payload: { index: number; newValue: string }): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMHeadersValue(payload));
	};

export const setCRMPayloadTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMPayloadType(payload));
	};

export const setCRMLeadIDFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMLeadIDField(payload));
	};

export const setCRMNameFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMNameField(payload));
	};

export const setCRMPhoneNumberFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMPhoneNumberField(payload));
	};

export const setCRMProjectNameFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMProjectNameField(payload));
	};

export const setCRMStatusFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMStatusField(payload));
	};

export const setCRMNotesFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMNotesField(payload));
	};

export const setCRMLeadSourceFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMLeadSourceField(payload));
	};

export const setCRMEnquiredAtFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMEnquiredAtField(payload));
	};

export const setCRMAPIKeyFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setCRMAPIKeyField(payload));
	};

export const fetchBusinessDashboardActivityLogsDataThunk =
	(payload: {
		business_profile_id: string;
		sessionId: string | ObjectId;
		filter_tab_value: string;
		filter_activity_by: string;
		filter_time_period: { start_date: string | Date; end_date: string | Date };
		page?: number;
		page_size?: number;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboardActivityLogsData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/activity-logs/fetch-activity-logs", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({
							...payload,
							page: payload.page || 1,
							page_size: payload.page_size || 20,
						}),
					})
				).json(),
			),
		);
	};

export const fetchBusinessDashboarMembersAndAdminsDataThunk =
	(payload: { business_profile_id: string; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchBusinessDashboarMembersAndAdminsData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/activity-logs/fetch-members-and-admins", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const loadMoreBusinessDashboardActivityLogsDataThunk =
	(payload: {
		business_profile_id: string;
		sessionId: string | ObjectId;
		filter_tab_value: string;
		filter_activity_by: string;
		filter_time_period: { start_date: string | Date; end_date: string | Date };
		page?: number;
		page_size?: number;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.loadMoreBusinessDashboardActivityLogsData(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "business-dashboard/activity-logs/fetch-activity-logs", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify({
							...payload,
							page: payload.page || 1,
							page_size: payload.page_size || 20,
						}),
					})
				).json(),
			),
		);
	};

export const setActivityLogsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setActivityLogsLoading(payload));
	};

/*
 * --- Manage Team (the dashboard's Team screen) ---
 *
 * Every route below lives in business-dashboard/business-profile-admins, authenticated by the
 * NextAuth session cookie (the caller is never sent in the body) and answering in the
 * standard envelope ({ status, payload, missingFields, error }). The rules are the app's
 * (lib/teams/teamMembership.ts, lib/business/ownershipTransfer.ts). The write thunks return
 * the envelope so the screen can show the outcome and re-fetch the list, which carries the
 * server's per-row action flags.
 */

/* Posts [payload] to a team route and returns the parsed envelope (or a generic error one). */
const postTeamRoute = async (route: string, payload: any): Promise<any> => {
	try {
		return await (
			await fetch(process.env.PRODUCTION_API_URL + "/business-dashboard/business-profile-admins/" + route, {
				method: "POST",
				cache: "no-store",
				headers: {
					"Content-Type": "text/plain",
				},
				body: JSON.stringify(payload),
			})
		).json();
	} catch {
		return { status: "error", payload: null, missingFields: null, error: "network" };
	}
};

export const fetchBusinessProfileAdminsThunk =
	(payload: { business_profile_id: ObjectId | string; page: number; limit: number; search_query?: string }): AppThunk =>
	async (dispatch) => {
		const response = await postTeamRoute("fetch-business-profile-admins", payload);
		dispatch(
			globalSlice.actions.fetchBusinessProfileAdmins({
				...response,
				request: { business_profile_id: String(payload.business_profile_id), page: payload.page },
			}),
		);
	};

export const addNewBusinessProfileAdminThunk =
	(payload: {
		business_profile_id: ObjectId | string;
		phones: Array<{ countryCode: string; number: string }>;
		role: string;
		access: string[];
		profile_context: StateInterface["profile_context"];
	}): AppThunk<Promise<any>> =>
	async () => {
		/* Answers { success, admins } or { error }; the screen re-fetches the list afterwards. */
		return await postTeamRoute("add-business-profile-admin", payload);
	};

export const removeBusinessProfileAdminThunk =
	(payload: { business_profile_id: ObjectId | string; user_id: string }): AppThunk<Promise<any>> =>
	async (dispatch) => {
		const response = await postTeamRoute("remove-business-profile-admin", payload);
		dispatch(globalSlice.actions.removeBusinessProfileAdmin(response));
		return response;
	};

/* Leaves the team as the session user; the screen then reloads the session (loading.html). */
export const exitBusinessProfileAdminThunk =
	(payload: { business_profile_id: ObjectId | string }): AppThunk<Promise<any>> =>
	async () => {
		return await postTeamRoute("exit-business-profile-admin", payload);
	};

export const updateBusinessProfileAdminRoleThunk =
	(payload: {
		business_profile_id: ObjectId | string;
		user_id: string;
		role: string;
		access: string[];
	}): AppThunk<Promise<any>> =>
	async (dispatch) => {
		const response = await postTeamRoute("update-business-profile-admin", payload);
		dispatch(globalSlice.actions.updateBusinessProfileAdminRole(response));
		return response;
	};

/* Cancels a pending invite, keyed by the invitee's country_code + number. */
export const cancelBusinessProfileInviteThunk =
	(payload: { business_profile_id: ObjectId | string; user_phone: string }): AppThunk<Promise<any>> =>
	async () => {
		return await postTeamRoute("cancel-invite", payload);
	};

/* Resends a pending invite (refreshes its sent date), keyed by country_code + number. */
export const resendBusinessProfileInviteThunk =
	(payload: { business_profile_id: ObjectId | string; user_phone: string }): AppThunk<Promise<any>> =>
	async () => {
		return await postTeamRoute("resend-invite", payload);
	};

/* Owner only: hands the business to an active member. Answers outcome "transferred" or "offer_sent". */
export const transferBusinessProfileOwnershipThunk =
	(payload: { business_profile_id: ObjectId | string; new_owner_user_id: string }): AppThunk<Promise<any>> =>
	async () => {
		return await postTeamRoute("transfer-ownership", payload);
	};

export const fetchKeyHighlightsThunk =
	(payload: { business_profile_id: string; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchKeyHighlights(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "localities/fetch-key-highlights", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchLocalitiesFaqThunk =
	(payload: { business_profile_id: string; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchLocalitiesFaq(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "localities/fetch-localities-faq", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchCoreDetailsAndImagesThunk =
	(payload: { business_profile_id: string; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchCoreDetailsAndImages(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "localities/fetch-core-details-and-images", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchCuratedPropertyIdsThunk =
	(payload: { business_profile_id: string; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchCuratedPropertyIds(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "localities/fetch-curated-property-ids", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchPriceTrendsThunk =
	(payload: { business_profile_id: string; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchPriceTrends(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "localities/fetch-price-trends", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const fetchMapDetailsAndVicinityThunk =
	(payload: { business_profile_id: string; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchMapDetailsAndVicinity(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "localities/fetch-map-details-and-vicinity", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateCurrencyThunk =
	(payload: { userId: string | ObjectId; currency: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateCurrency(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "account-settings/update-currency", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateCookieConsentThunk =
	(payload: {
		userId: string | ObjectId;
		consent: {
			necessary: boolean;
			analytics: boolean;
			marketing: boolean;
			other: boolean;
		};
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateCookieConsent(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "user-profile/cookie-consent", {
						method: "POST",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

/*

& Team invites (10 Oct 2026). The routes identify the invitee from the NextAuth session
& only, so nothing here sends a user id or phone. Each thunk answers a TeamInviteCallResult
& (never throws) so the sheet can tell a stale invite or a switch confirmation from success.

*/

/** What a team-invite route answered: the HTTP outcome, the payload and the error string. */
export type TeamInviteCallResult = { ok: boolean; status: number; payload: any; error: string | null };

/* POSTs JSON to a pages/api/teams route; a network failure answers status 0. */
const postTeamInviteRoute = async (route: string, body: any): Promise<TeamInviteCallResult> => {
	try {
		const response = await fetch("/api/teams/" + route, {
			method: "POST",
			cache: "no-store",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		});
		const data: any = await response.json().catch(() => null);
		return { ok: response.ok, status: response.status, payload: data?.payload ?? null, error: data?.error ?? null };
	} catch {
		return { ok: false, status: 0, payload: null, error: null };
	}
};

/* Fetches the `/?invite=<token>` invite and stores it in team_invite (which opens the home page's sheet). */
export const fetchTeamInviteThunk =
	(invite_token: string): AppThunk<Promise<TeamInviteCallResult>> =>
	async (dispatch) => {
		const result = await postTeamInviteRoute("fetch-invite", { invite_token });
		if (result.ok && result.payload) {
			dispatch(globalSlice.actions.SetTeamInvite(result.payload));
		}
		return result;
	};

/* Fetches an invite for a notification row, by token or by the inviting business, without storing it. */
export const fetchTeamInviteDetailsThunk =
	(target: { invite_token?: string; business_profile_id?: string }): AppThunk<Promise<TeamInviteCallResult>> =>
	async () => {
		return await postTeamInviteRoute("fetch-invite", target);
	};

/* Accepts an invite as the session user; confirm: true after the switch step. */
export const acceptTeamInviteThunk =
	(invite_token: string, confirm: boolean = false): AppThunk<Promise<TeamInviteCallResult>> =>
	async (dispatch) => {
		const result = await postTeamInviteRoute("accept-invite", { invite_token, confirm });
		if (result.ok && result.payload) {
			dispatch(globalSlice.actions.SetPlatformAccessLevel(result.payload));
		}
		return result;
	};

/* Declines an invite as the session user. */
export const declineTeamInviteThunk =
	(invite_token: string): AppThunk<Promise<TeamInviteCallResult>> =>
	async () => {
		return await postTeamInviteRoute("decline-invite", { invite_token });
	};

/** One person ownership of a business can go to: an active admin or member, not the caller. */
export type TransferCandidate = { user_id: string; name: string; phone: string; role: string };

/** A business's transfer picture: who it can go to, how many joined members it has, and the caller's role. */
export type TransferCandidates = { candidates: Array<TransferCandidate>; team_size: number; viewer_role: string };

/*
  The active admins and members of [business_profile_id] the caller (its owner) may hand it
  to, read through the Team screen's fetch route without touching the dashboard's team state.
  team_size counts every joined admin and member (the owner holds no row). Null when the
  list could not be read.
*/
export const fetchTransferCandidatesThunk =
	(business_profile_id: string): AppThunk<Promise<TransferCandidates | null>> =>
	async () => {
		const response = await postTeamRoute("fetch-business-profile-admins", { business_profile_id, page: 1, limit: 100 });
		const rows: Array<any> | null = response?.status === "success" ? (response?.payload?.admins ?? null) : null;
		if (!rows) return null;
		const joined = rows.filter(
			(row: any) => row && row.status !== "invite-pending" && String(row.role).toLowerCase() !== "owner" && !!row.user_id,
		);
		return {
			candidates: joined
				.filter((row: any) => !row.is_self)
				.map((row: any) => ({
					user_id: String(row.user_id),
					name: `${row.first_name || ""} ${row.last_name || ""}`.trim() || row.phone || row.user_phone || "Team member",
					phone: row.phone || row.user_phone || "",
					role: String(row.role || "").toLowerCase() === "admin" ? "Admin" : "Member",
				})),
			team_size: joined.length,
			viewer_role: String(response?.payload?.viewer?.role || "").toLowerCase(),
		};
	};

/* ======================== */
/* ? Requirements Thunks    */
/* ======================== */

/* Returns the API response so callers can tell success from failure (PostRequirementFlow's retry state) */

export const createRequirementThunk =
	(payload: any): AppThunk<Promise<any>> =>
	async (dispatch) => {
		const response = await (
			await fetch(process.env.PRODUCTION_API_URL + "requirements/create-requirement", {
				method: "POST",
				cache: "no-store",
				headers: { "Content-Type": "text/plain" },
				body: JSON.stringify(payload),
			})
		).json();
		dispatch(globalSlice.actions.createRequirement(response));
		return response;
	};

export const fetchMyRequirementsThunk =
	(payload: {
		business_profile_id: string;
		filter_status?: string;
		page?: number;
		limit?: number;
		sort_field?: string;
		sort_order?: string;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchMyRequirements({
				...(await (
					await fetch(process.env.PRODUCTION_API_URL + "requirements/fetch-my-requirements", {
						method: "POST",
						cache: "no-store",
						headers: { "Content-Type": "text/plain" },
						body: JSON.stringify(payload),
					})
				).json()),
				page: payload.page || 1,
			}),
		);
	};

/* Requirements posted by an individual user (user profile page). Reuses the my_requirements state. */

export const fetchUserRequirementsThunk =
	(payload: {
		user_id: string;
		filter_status?: string;
		page?: number;
		limit?: number;
		sort_field?: string;
		sort_order?: string;
	}): AppThunk<Promise<string>> =>
	async (dispatch) => {
		try {
			const response = await (
				await fetch(process.env.PRODUCTION_API_URL + "requirements/fetch-user-requirements", {
					method: "POST",
					cache: "no-store",
					headers: { "Content-Type": "text/plain" },
					body: JSON.stringify(payload),
				})
			).json();
			dispatch(globalSlice.actions.fetchMyRequirements(response));
			return response && response.status ? response.status : "failure";
		} catch (error) {
			dispatch(globalSlice.actions.fetchMyRequirements({ status: "failure" }));
			return "failure";
		}
	};

export const fetchSingleRequirementThunk =
	(payload: { requirement_id: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchSingleRequirement(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "requirements/fetch-requirement", {
						method: "POST",
						cache: "no-store",
						headers: { "Content-Type": "text/plain" },
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateRequirementStatusThunk =
	(payload: { requirement_id: string; new_status: string; changed_by?: any }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateRequirementStatus(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "requirements/update-requirement-status", {
						method: "POST",
						cache: "no-store",
						headers: { "Content-Type": "text/plain" },
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const updateRequirementThunk =
	(payload: any): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.updateRequirement(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "requirements/update-requirement", {
						method: "POST",
						cache: "no-store",
						headers: { "Content-Type": "text/plain" },
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const closeRequirementThunk =
	(payload: { requirement_id: string; changed_by?: any }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.closeRequirement(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "requirements/close-requirement", {
						method: "POST",
						cache: "no-store",
						headers: { "Content-Type": "text/plain" },
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

/* Extend, repost or delete one of the business's requirements (the app's rules, lib/requirements/*RequirementCore.ts,
   behind the website's requirements/{extend,repost,delete}-requirement), then reload My Requirements' first page with
   its filter, so the card's state and the status chips' counts follow. Answers { ok, error } for the screen to show. */

export const requirementActionThunk =
	(payload: {
		action: "extend" | "repost" | "delete";
		requirement_id: string;
		business_profile_id: string;
		extend_days?: number;
	}): AppThunk<Promise<{ ok: boolean; error: string }>> =>
	async (dispatch, getState) => {
		let result: any = null;
		try {
			result = await (
				await fetch(process.env.PRODUCTION_API_URL + `requirements/${payload.action}-requirement`, {
					method: "POST",
					cache: "no-store",
					headers: { "Content-Type": "text/plain" },
					body: JSON.stringify({
						requirement_id: payload.requirement_id,
						business_profile_id: payload.business_profile_id,
						...(payload.action === "extend" ? { extend_days: payload.extend_days } : {}),
					}),
				})
			).json();
		} catch {
			return { ok: false, error: "Something went wrong. Please try again." };
		}
		if (result?.status !== "success") return { ok: false, error: result?.error || "Something went wrong. Please try again." };
		const myRequirements = (getState().globalState as unknown as StateInterface)?.my_requirements;
		dispatch(
			fetchMyRequirementsThunk({
				business_profile_id: payload.business_profile_id,
				filter_status: myRequirements?.filter_status || "",
				page: 1,
				sort_field: myRequirements?.filter_sort_field || "created_at",
				sort_order: myRequirements?.filter_sort_order || "desc",
			}),
		);
		return { ok: true, error: "" };
	};

export const setMyRequirementsFilterStatusThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setMyRequirementsFilterStatus(payload));
	};

export const setMyRequirementsFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setMyRequirementsFilterPageNumber(payload));
	};

export const setMyRequirementsFilterSortFieldThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setMyRequirementsFilterSortField(payload));
	};

export const setMyRequirementsFilterSortOrderThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setMyRequirementsFilterSortOrder(payload));
	};

export const setMyRequirementsTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setMyRequirementsTriggerNewSearch(payload));
	};

export const setMyRequirementsLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setMyRequirementsLoading(payload));
	};

export const setMyRequirementsInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setMyRequirementsInitialLoadDone(payload));
	};

/* ================================ */
/* ? Requirements Feed Thunks       */
/* ================================ */

export const fetchRequirementsFeedThunk =
	(payload: {
		business_profile_id: string;
		mode?: string;
		page?: number;
		limit?: number;
		sort_field?: string;
		sort_order?: string;
		filter_transaction_type?: string;
		filter_property_category?: string;
		filter_location?: string;
		filter_poster_type?: string;
		filter_budget_min?: number;
		filter_budget_max?: number;
	}): AppThunk =>
	async (dispatch) => {
		const data = await (
			await fetch(process.env.PRODUCTION_API_URL + "requirements/fetch-requirements-feed", {
				method: "POST",
				cache: "no-store",
				headers: { "Content-Type": "text/plain" },
				body: JSON.stringify(payload),
			})
		).json();
		dispatch(globalSlice.actions.fetchRequirementsFeed({ ...data, mode: payload.mode || "feed", page: payload.page || 1 }));
	};

/* The feed through requirements/fetch-requirements-feed-v2 (lib/requirements/feedV2Core.ts, the app's rule): the chips,
   the filter sheet, the sorts, "Only matches" and the discovery rails. The list and its paging go through the same
   reducer as before; the rails come back to the screen. The tab counts stay with fetchRequirementsFeedCountsThunk, so
   the reducer is told "feed_v2" / "claimed" (counts it doesn't touch) rather than "feed" / "responded". */

export const fetchRequirementsFeedV2Thunk =
	(payload: {
		business_profile_id: string;
		mode: "feed" | "claimed" | "saved";
		page?: number;
		type?: string;
		sort_by?: string;
		filter_property_category?: string;
		filter_property_type?: string;
		filter_poster_type?: string;
		filter_locations?: Array<string>;
		rail?: string;
	}): AppThunk<Promise<any>> =>
	async (dispatch) => {
		let data: any = null;
		try {
			data = await (
				await fetch(process.env.PRODUCTION_API_URL + "requirements/fetch-requirements-feed-v2", {
					method: "POST",
					cache: "no-store",
					headers: { "Content-Type": "text/plain" },
					body: JSON.stringify(payload),
				})
			).json();
		} catch {
			data = { status: "failure", payload: null, error: "Network error" };
		}
		dispatch(
			globalSlice.actions.fetchRequirementsFeed({
				...data,
				mode: payload.mode === "feed" ? "feed_v2" : payload.mode,
				page: payload.page || 1,
				rail: payload.rail || "",
			}),
		);
		return data;
	};

export const fetchRequirementsFeedCountsThunk =
	(payload: { business_profile_id: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.fetchRequirementsFeedCounts(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "requirements/fetch-requirements-feed-counts", {
						method: "POST",
						cache: "no-store",
						headers: { "Content-Type": "text/plain" },
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const respondToRequirementThunk =
	(payload: {
		requirement_id: string;
		claimer_user_id: string;
		claimer_business_profile_id: string;
		business_profile_type?: string;
		claimer_role?: string;
		attached_listing?: Array<string>;
		/* A paid claim's Razorpay payment (requirements/create-claim-order, then verify-claim-payment). */
		razorpay_payment_id?: string;
	}): AppThunk<Promise<any>> =>
	async (dispatch) => {
		const data = await (
			await fetch(process.env.PRODUCTION_API_URL + "requirements/respond-to-requirement", {
				method: "POST",
				cache: "no-store",
				headers: { "Content-Type": "text/plain" },
				body: JSON.stringify(payload),
			})
		).json();
		dispatch(globalSlice.actions.respondToRequirement(data));
		return data;
	};

export const saveRequirementThunk =
	(payload: {
		requirement_id: string;
		user_id: string;
		is_business_profile?: boolean;
		business_profile_id?: string;
		entity_created_by?: any;
	}): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.saveRequirement(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "requirements/save-requirement", {
						method: "POST",
						cache: "no-store",
						headers: { "Content-Type": "text/plain" },
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const unsaveRequirementThunk =
	(payload: { requirement_id: string; user_id: string; business_profile_id?: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.unsaveRequirement(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "requirements/unsave-requirement", {
						method: "POST",
						cache: "no-store",
						headers: { "Content-Type": "text/plain" },
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const trackContactActionThunk =
	(payload: { claim_id: string; action: string; chat_id?: string }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.trackContactAction(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "requirements/track-contact-action", {
						method: "POST",
						cache: "no-store",
						headers: { "Content-Type": "text/plain" },
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

export const setRequirementsFeedFilterTransactionTypeThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setRequirementsFeedFilterTransactionType(payload));
	};

export const setRequirementsFeedFilterPropertyCategoryThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setRequirementsFeedFilterPropertyCategory(payload));
	};

export const setRequirementsFeedFilterLocationThunk =
	(payload: string): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setRequirementsFeedFilterLocation(payload));
	};

export const setRequirementsFeedFilterPageNumberThunk =
	(payload: number): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setRequirementsFeedFilterPageNumber(payload));
	};

export const setRequirementsFeedLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setRequirementsFeedLoading(payload));
	};

export const setRequirementsFeedInitialLoadDoneThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setRequirementsFeedInitialLoadDone(payload));
	};

export const setRequirementsFeedTriggerNewSearchThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setRequirementsFeedTriggerNewSearch(payload));
	};

export const setOpenCreateRequirementModalThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setOpenCreateRequirementModal(payload));
	};

export const setMyRequirementsSelectedRequirementLoadingThunk =
	(payload: boolean): AppThunk =>
	(dispatch) => {
		dispatch(globalSlice.actions.setMyRequirementsSelectedRequirementLoading(payload));
	};

/*
& Sales Support Investment thunk - fetches a single sales support investment and sets it in state.
*/

export const FetchSalesSupportInvestmentThunk =
	(payload: { investment_id: string | ObjectId; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.SetSalesSupportInvestment(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "sales-support/fetch-investment", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};

/*
& Property Closure thunk - fetches a single property closure and sets it in state.
*/

export const FetchPropertyClosureThunk =
	(payload: { closure_id: string | ObjectId; sessionId: string | ObjectId }): AppThunk =>
	async (dispatch) => {
		dispatch(
			globalSlice.actions.SetPropertyClosure(
				await (
					await fetch(process.env.PRODUCTION_API_URL + "property-closures/fetch-closure", {
						method: "POST",
						cache: "no-store",
						body: JSON.stringify(payload),
					})
				).json(),
			),
		);
	};
